import React, { useState, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FiUser, 
  FiLogOut, 
  FiEdit2,
  FiTrash2,
  FiPlus,
  FiFilter,
  FiBookmark,
  FiSave,
  FiCopy,
  FiMessageSquare,
  FiShield
} from 'react-icons/fi';
import AnnouncementModal from './AnnouncementModal';
import './DashboardPage.css';

const QUICK_DURATION_OPTIONS = [
  { value: 5, label: '5分钟' },
  { value: 10, label: '10分钟' },
  { value: 20, label: '20分钟' },
  { value: 30, label: '30分钟' },
  { value: 60, label: '1小时' },
  { value: 120, label: '2小时' },
  { value: 240, label: '4小时' },
  { value: 480, label: '8小时' }
];

const QUICK_ROUND_OPTIONS = [
  { value: 5, label: '5分钟' },
  { value: 10, label: '10分钟' },
  { value: 30, label: '30分钟' },
  { value: 60, label: '1小时' }
];

const formatDurationLabel = (totalMinutes) => {
  if (totalMinutes < 60) {
    return `${totalMinutes}分钟`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (minutes === 0) {
    return `${hours}小时`;
  }

  return `${hours}小时${minutes}分钟`;
};

const formatTemplateDateTime = (value) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};

// 数据状态管理 - 移除了硬编码的模拟数据，改为从API获取真实数据

const DashboardPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('C2');
  const [c2Data, setC2Data] = useState([]);
  const [b3Data, setB3Data] = useState([]);
  const [filters, setFilters] = useState({
    line: '',
    workCode: '',
    department: '',
    station: '',
    content: '',
    area: '',
    supervisor: '',
    status: ''
  });
  const [lines, setLines] = useState([]);
  const [stations, setStations] = useState([]);
  const [depots, setDepots] = useState([]);
  const [selectedLine, setSelectedLine] = useState('');
  const [showMyData, setShowMyData] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [isLoadingOAData, setIsLoadingOAData] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSupervisorModal, setShowSupervisorModal] = useState(false);
  const [supervisorCandidates, setSupervisorCandidates] = useState([]);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [formData, setFormData] = useState({
    line: '',
    workDate: '',
    startTime: '',
    endTime: '',
    isStartNextDay: false,
    isEndNextDay: false,
    workTitle: '',
    station: '',
    supervisor: '',
    workerCount: '',
    workArea: '',
    workContent: '',
    safetyMeasures: '',
    remarks: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userTemplates, setUserTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({
    type: '功能建议',
    content: ''
  });
  const [isFeedbackSubmitting, setIsFeedbackSubmitting] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState(null);
  const [copyingItemId, setCopyingItemId] = useState(null);
  const [showPublicTemplatesModal, setShowPublicTemplatesModal] = useState(false);
  const [publicTemplates, setPublicTemplates] = useState([]);
  const [isLoadingPublicTemplates, setIsLoadingPublicTemplates] = useState(false);
  const [showCopyTemplateModal, setShowCopyTemplateModal] = useState(false);
  const [selectedPublicTemplate, setSelectedPublicTemplate] = useState(null);
  const [copyTemplateName, setCopyTemplateName] = useState('');
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [showFaultModal, setShowFaultModal] = useState(false);
  const [maintenanceOrders, setMaintenanceOrders] = useState([]);
  const [faultOrders, setFaultOrders] = useState([]);
  const [selectedMaintenanceOrders, setSelectedMaintenanceOrders] = useState([]);
  const [selectedFaultOrders, setSelectedFaultOrders] = useState([]);
  const [isLoadingMaintenanceOrders, setIsLoadingMaintenanceOrders] = useState(false);
  const [isLoadingFaultOrders, setIsLoadingFaultOrders] = useState(false);
  const [announcement, setAnnouncement] = useState(null);
  const [userTemplateSearchText, setUserTemplateSearchText] = useState('');
  const [publicTemplateSearchText, setPublicTemplateSearchText] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [originalEditData, setOriginalEditData] = useState(null);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [isGlobalButtonsDisabled, setIsGlobalButtonsDisabled] = useState(false);
  const [selectedQuickDurations, setSelectedQuickDurations] = useState([]);
  const totalSelectedDuration = selectedQuickDurations.reduce((total, current) => total + current, 0);
  const selectedDurationText = totalSelectedDuration > 0 ? formatDurationLabel(totalSelectedDuration) : '';

  // 根据当前选中的标签页获取对应的数据
  const getCurrentData = () => {
    return activeTab === 'C2' ? c2Data : b3Data;
  };

  // 删除工作项
  const removeWorkItem = (uniqueId) => {
    if (activeTab === 'C2') {
      setC2Data(prevData => 
        prevData.filter(dataItem => dataItem.uniqueId !== uniqueId)
      );
    } else {
      setB3Data(prevData => 
        prevData.filter(dataItem => dataItem.uniqueId !== uniqueId)
      );
    }
  };

  // 处理会话过期
  const handleSessionExpired = useCallback(() => {
    // 清除所有toast提示
    toast.dismiss();
    
    // 清除本地存储的用户信息
    localStorage.removeItem('userInfo');
    setUserInfo(null);
    
    // 延迟跳转，让用户看到提示信息
    setTimeout(() => {
      navigate('/login', { 
        state: { 
          message: '会话已过期，请重新登录' 
        } 
      });
    }, 2000);
  }, [navigate]);

  // 获取线路数据
  const fetchLines = useCallback(async () => {
    try {
      const response = await fetch('/api/lines');
      
      if (response.status === 401) {
        handleSessionExpired();
        return;
      }
      
      const result = await response.json();
      if (result.success) {
        setLines(result.data);
      }
    } catch (error) {
      console.error('获取线路数据失败:', error);
    }
  }, [handleSessionExpired]);

  // 获取用户模板
  const fetchUserTemplates = useCallback(async (username) => {
    if (!username) return;
    
    setIsLoadingTemplates(true);
    try {
      const response = await fetch(`/api/templates/${encodeURIComponent(username)}`);
      
      if (response.status === 401) {
        handleSessionExpired();
        return;
      }
      
      const result = await response.json();
      
      if (result.success) {
        setUserTemplates(result.templates);
      } else {
        console.error('获取模板失败:', result.message);
      }
    } catch (error) {
      console.error('获取模板失败:', error);
    } finally {
      setIsLoadingTemplates(false);
    }
  }, [handleSessionExpired]);

  const getFilteredUserTemplates = () => {
    if (!userTemplateSearchText.trim()) {
      return userTemplates;
    }

    const searchText = userTemplateSearchText.toLowerCase();
    return userTemplates.filter(template => {
      return (
        template.name.toLowerCase().includes(searchText) ||
        template.workTitle.toLowerCase().includes(searchText) ||
        template.workArea.toLowerCase().includes(searchText) ||
        template.workContent.toLowerCase().includes(searchText) ||
        template.safetyMeasures.toLowerCase().includes(searchText) ||
        template.remarks.toLowerCase().includes(searchText)
      );
    });
  };

  // 获取施工作业台账数据
  const fetchOAData = async () => {
    if (!userInfo?.userName) {
      toast.error('用户信息不完整，请重新登录');
      handleSessionExpired();
      return;
    }

    // 显示加载toast
    const loadingToast = toast.loading('正在刷新数据，请稍候...');
    setIsLoadingOAData(true);
    try {
      const response = await fetch(`/api/oa-data?userName=${encodeURIComponent(userInfo.userName)}&type=${activeTab}`);
      
      if (response.status === 401) {
        // 会话过期，处理登录过期
        toast.dismiss(loadingToast);
        handleSessionExpired();
        return;
      }
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      // 关闭加载toast
      toast.dismiss(loadingToast);
      
      if (result.success) {
        toast.success(`${result.data.type}类施工作业台账数据获取成功`);
        
        // 处理获取到的真实数据
        const realData = result.data.records || [];
        
        if (activeTab === 'C2') {
          setC2Data(realData);
        } else if (activeTab === 'B3') {
          setB3Data(realData);
        }
        
        console.log(`${result.data.type}类数据:`, {
          totalCount: result.data.totalCount,
          pageSize: result.data.pageSize,
          records: realData.length
        });
      } else {
        toast.error(result.message || '施工作业台账数据获取失败');
      }
    } catch (error) {
      console.error('获取施工作业台账数据失败:', error);
      toast.dismiss(loadingToast);
      toast.error('网络错误，请检查连接后重试');
    } finally {
      setIsLoadingOAData(false);
    }
  };

  // 根据线路获取车站/车场数据
  const fetchStationsAndDepots = async (line) => {
    try {
      const [stationsResponse, depotsResponse] = await Promise.all([
        fetch(`/api/stations/${encodeURIComponent(line)}`),
        fetch(`/api/depots/${encodeURIComponent(line)}`)
      ]);
      
      // 检查会话状态
      if (stationsResponse.status === 401 || depotsResponse.status === 401) {
        handleSessionExpired();
        return;
      }
      
      const stationsResult = await stationsResponse.json();
      const depotsResult = await depotsResponse.json();
      
      if (stationsResult.success) {
        setStations(stationsResult.data);
      }
      if (depotsResult.success) {
        setDepots(depotsResult.data);
      }
    } catch (error) {
      console.error('获取车站/车场数据失败:', error);
    }
  };

  // 处理线路选择变化
  const handleLineChange = (line) => {
    setSelectedLine(line);
    setFilters(prev => ({ ...prev, line, station: '' })); // 清空车站选择
    if (line) {
      fetchStationsAndDepots(line);
    } else {
      setStations([]);
      setDepots([]);
    }
  };

  // 筛选数据
  const getFilteredData = () => {
    let data = getCurrentData();
    
    // 如果开启了"只显示我的数据"，先按施工负责人筛选
    if (showMyData && userInfo?.name) {
      data = data.filter(item => item.supervisor && item.supervisor.includes(userInfo.name));
    }
    
    // 应用其他筛选条件
    return data.filter(item => {
      return (
        (!filters.line || item.line.includes(filters.line)) &&
        (!filters.workCode || item.workCode.includes(filters.workCode)) &&
        (!filters.department || item.department.includes(filters.department)) &&
        (!filters.station || item.station.includes(filters.station)) &&
        (!filters.content || item.content.includes(filters.content)) &&
        (!filters.area || item.area.includes(filters.area)) &&
        (!filters.supervisor || item.supervisor.includes(filters.supervisor)) &&
        (!filters.status || item.status === filters.status)
      );
    });
  };

  const constructionData = getFilteredData();

  // 获取公告
  const fetchAnnouncement = useCallback(async () => {
    try {
      const response = await fetch('/api/announcement');
      
      if (response.status === 401) {
        handleSessionExpired();
        return;
      }
      
      const result = await response.json();
      
      if (result.success) {
        setAnnouncement(result.data);
      } else {
        console.error('获取公告失败:', result.message);
      }
    } catch (error) {
      console.error('获取公告失败:', error);
    }
  }, [handleSessionExpired]);

  const fetchAdminAccess = useCallback(async (username) => {
    if (!username) {
      return;
    }

    try {
      const response = await fetch(`/api/admin/access/${encodeURIComponent(username)}`);

      if (response.status === 401) {
        handleSessionExpired();
        return;
      }

      const isAdmin = response.ok;

      setUserInfo(prev => {
        if (!prev) {
          return prev;
        }

        const nextUserInfo = {
          ...prev,
          isAdmin
        };

        localStorage.setItem('userInfo', JSON.stringify(nextUserInfo));
        return nextUserInfo;
      });
    } catch (error) {
      console.error('获取管理员权限失败:', error);
    }
  }, [handleSessionExpired]);

  useEffect(() => {
    // 从路由状态或localStorage获取用户信息
    let userData = null;
    
    if (location.state && location.state.userName) {
      userData = {
        userName: location.state.userName,
        name: location.state.name || location.state.userName,
        redirectUrl: location.state.redirectUrl,
        loginTime: new Date().toISOString(),
        isAdmin: Boolean(location.state.isAdmin)
      };
      // 保存到localStorage
      localStorage.setItem('userInfo', JSON.stringify(userData));
    } else {
      // 从localStorage获取
      const savedUserInfo = localStorage.getItem('userInfo');
      if (savedUserInfo) {
        userData = JSON.parse(savedUserInfo);
      }
    }
    
    if (!userData) {
      toast.error('未找到用户信息，请重新登录');
      navigate('/login');
      return;
    }
    
    setUserInfo(userData);
    // 组件初始化时不再使用模拟数据，改为通过API获取真实数据
    // 初始化线路数据
    fetchLines();
    // 获取用户模板
    fetchUserTemplates(userData.userName);
    // 获取公告
    fetchAnnouncement();
    fetchAdminAccess(userData.userName);
  }, [location.state, navigate, fetchLines, fetchUserTemplates, fetchAnnouncement, fetchAdminAccess]);

  // 监听标签页切换，重置车站选择
  useEffect(() => {
    // 只在标签页切换时检查，避免在编辑模式下误清空车站数据
    // 如果正在编辑或者stations/depots为空，则跳过检查
    if (showEditModal || editingItemId || stations.length === 0 || depots.length === 0) {
      return;
    }
    
    // 当切换标签页时，检查当前选择的车站是否在新标签页对应的数据中存在
    if (formData.station && formData.line) {
      const currentStationList = activeTab === 'C2' ? stations : depots;
      const isStationValid = currentStationList.includes(formData.station);
      
      if (!isStationValid) {
        // 如果当前选择的车站在新标签页中不存在，清空车站选择
        setFormData(prev => ({
          ...prev,
          station: ''
        }));
        
        // 清除车站字段的错误状态
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.station;
          return newErrors;
        });
      }
    }
  }, [activeTab, formData.station, formData.line, stations, depots, showEditModal, editingItemId]);

  // 保存模板
  const saveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('请输入模板名称');
      return;
    }
    
    if (!formData.workTitle || !formData.workArea || !formData.workContent || !formData.safetyMeasures || !formData.remarks) {
      toast.error('请先填写完整的模板内容');
      return;
    }
    
    // 检查是否存在同名模板
    const existingTemplate = userTemplates.find(template => template.name === templateName.trim());
    if (existingTemplate) {
      const confirmOverwrite = window.confirm(`已存在名为"${templateName.trim()}"的模板，是否要覆盖？`);
      if (!confirmOverwrite) {
        return;
      }
    }
    
    try {
      const response = await fetch(`/api/templates/${encodeURIComponent(userInfo.userName)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: templateName.trim(),
          workTitle: formData.workTitle,
          workArea: formData.workArea,
          workContent: formData.workContent,
          safetyMeasures: formData.safetyMeasures,
          remarks: formData.remarks
        })
      });
      
      if (response.status === 401) {
        handleSessionExpired();
        return;
      }
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(result.message);
        setShowSaveTemplateModal(false);
        setTemplateName('');
        // 重新获取模板列表
        fetchUserTemplates(userInfo.userName);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('保存模板失败:', error);
      toast.error('保存模板失败');
    }
  };

  const handleFeedbackFormChange = (field, value) => {
    setFeedbackForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetFeedbackForm = () => {
    setFeedbackForm({
      type: '功能建议',
      content: ''
    });
  };

  const closeFeedbackModal = () => {
    if (isFeedbackSubmitting) {
      return;
    }

    setShowFeedbackModal(false);
    resetFeedbackForm();
  };

  const submitFeedback = async () => {
    const content = feedbackForm.content.trim();

    if (!content) {
      toast.error('请输入意见或建议内容');
      return;
    }

    if (content.length < 5) {
      toast.error('意见或建议内容至少填写5个字');
      return;
    }

    setIsFeedbackSubmitting(true);

    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: userInfo.userName,
          name: userInfo.name,
          type: feedbackForm.type,
          content
        })
      });

      if (response.status === 401) {
        handleSessionExpired();
        return;
      }

      const result = await response.json();

      if (result.success) {
        toast.success(result.message || '感谢你的反馈');
        setShowFeedbackModal(false);
        resetFeedbackForm();
      } else {
        toast.error(result.message || '提交失败，请稍后重试');
      }
    } catch (error) {
      console.error('提交意见建议失败:', error);
      toast.error('提交失败，请稍后重试');
    } finally {
      setIsFeedbackSubmitting(false);
    }
  };

  const openUserTemplatesModal = () => {
    setShowTemplateModal(true);
    setUserTemplateSearchText('');
    fetchUserTemplates(userInfo?.userName);
  };

  // 应用模板
  const applyTemplate = async (template) => {
    setFormData(prev => ({
      ...prev,
      workTitle: template.workTitle,
      workArea: template.workArea,
      workContent: template.workContent,
      safetyMeasures: template.safetyMeasures,
      remarks: template.remarks
    }));
    
    // 清除相关字段的错误状态
    setFormErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.workTitle;
      delete newErrors.workArea;
      delete newErrors.workContent;
      delete newErrors.safetyMeasures;
      delete newErrors.remarks;
      return newErrors;
    });
    
    setShowTemplateModal(false);
    toast.success(`已应用模板：${template.name}`);

    try {
      const response = await fetch(`/api/templates/${encodeURIComponent(userInfo.userName)}/${template.id}/use`, {
        method: 'POST'
      });

      if (response.status === 401) {
        handleSessionExpired();
        return;
      }

      const result = await response.json();
      if (result.success) {
        fetchUserTemplates(userInfo.userName);
      }
    } catch (error) {
      console.error('更新模板使用记录失败:', error);
    }
  };

  // 删除模板
  const deleteTemplate = async (templateId, templateName) => {
    if (!window.confirm(`确定要删除模板"${templateName}"吗？`)) {
      return;
    }
    
    try {
      const response = await fetch(`/api/templates/${encodeURIComponent(userInfo.userName)}/${templateId}`, {
        method: 'DELETE'
      });
      
      if (response.status === 401) {
        handleSessionExpired();
        return;
      }
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(result.message);
        // 重新获取模板列表
        fetchUserTemplates(userInfo.userName);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('删除模板失败:', error);
      toast.error('删除模板失败');
    }
  };

  // 分享模板
  const shareTemplate = async (templateId, templateName) => {
    try {
      const response = await fetch(`/api/templates/${encodeURIComponent(userInfo.userName)}/${templateId}/share`, {
        method: 'POST'
      });
      
      if (response.status === 401) {
        handleSessionExpired();
        return;
      }
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(`模板"${templateName}"分享成功`);
        // 重新获取模板列表
        fetchUserTemplates(userInfo.userName);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('分享模板失败:', error);
      toast.error('分享模板失败');
    }
  };

  // 取消分享模板
  const unshareTemplate = async (templateId, templateName) => {
    if (!window.confirm(`确定要取消分享模板"${templateName}"吗？`)) {
      return;
    }
    
    try {
      const response = await fetch(`/api/templates/${encodeURIComponent(userInfo.userName)}/${templateId}/share`, {
        method: 'DELETE'
      });
      
      if (response.status === 401) {
        handleSessionExpired();
        return;
      }
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(`模板"${templateName}"取消分享成功`);
        // 重新获取模板列表
        fetchUserTemplates(userInfo.userName);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('取消分享模板失败:', error);
      toast.error('取消分享模板失败');
    }
  };

  // 获取公共模板
  const fetchPublicTemplates = useCallback(async () => {
    if (!userInfo?.userName) return;
    
    setIsLoadingPublicTemplates(true);
    try {
      const response = await fetch(`/api/public-templates/${encodeURIComponent(userInfo.userName)}`);
      
      if (response.status === 401) {
        handleSessionExpired();
        return;
      }
      
      const result = await response.json();
      
      if (result.success) {
        setPublicTemplates(result.templates);
      } else {
        console.error('获取公共模板失败:', result.message);
      }
    } catch (error) {
      console.error('获取公共模板失败:', error);
    } finally {
      setIsLoadingPublicTemplates(false);
    }
  }, [userInfo?.userName, handleSessionExpired]);



  // 筛选公共模板
  const getFilteredPublicTemplates = () => {
    if (!publicTemplateSearchText.trim()) {
      return publicTemplates;
    }
    
    const searchText = publicTemplateSearchText.toLowerCase();
    return publicTemplates.filter(template => {
      return (
        template.name.toLowerCase().includes(searchText) ||
        template.author.toLowerCase().includes(searchText) ||
        template.workTitle.toLowerCase().includes(searchText) ||
        template.workArea.toLowerCase().includes(searchText) ||
        template.workContent.toLowerCase().includes(searchText) ||
        template.safetyMeasures.toLowerCase().includes(searchText) ||
        template.remarks.toLowerCase().includes(searchText)
      );
    });
  };

  // 打开公共模板模态框
  const openPublicTemplatesModal = () => {
    setShowPublicTemplatesModal(true);
    setPublicTemplateSearchText(''); // 重置搜索文本
    fetchPublicTemplates();
  };

  // 打开复制模板模态框
  const openCopyTemplateModal = (template) => {
    setSelectedPublicTemplate(template);
    setCopyTemplateName(`${template.name} - 副本`);
    setShowCopyTemplateModal(true);
  };

  // 复制公共模板
  const copyPublicTemplate = async () => {
    if (!copyTemplateName.trim()) {
      toast.error('请输入模板名称');
      return;
    }
    
    if (!selectedPublicTemplate) {
      toast.error('未选择模板');
      return;
    }
    
    try {
      const response = await fetch(`/api/templates/${encodeURIComponent(userInfo.userName)}/copy/${selectedPublicTemplate.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          newName: copyTemplateName.trim()
        })
      });
      
      if (response.status === 401) {
        handleSessionExpired();
        return;
      }
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(`模板"${copyTemplateName.trim()}"复制成功`);
        setShowCopyTemplateModal(false);
        setCopyTemplateName('');
        setSelectedPublicTemplate(null);
        // 重新获取用户模板列表
        fetchUserTemplates(userInfo.userName);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('复制模板失败:', error);
      toast.error('复制模板失败');
    }
  };

  // 打开检修工单模态框并直接查询
  const openMaintenanceModal = () => {
    setMaintenanceOrders([]);
    setSelectedMaintenanceOrders([]);
    setShowMaintenanceModal(true);
    // 直接执行查询
    queryMaintenanceOrders();
  };

  // 打开故障工单模态框并直接查询
  const openFaultModal = () => {
    setFaultOrders([]);
    setSelectedFaultOrders([]);
    setShowFaultModal(true);
    // 直接执行查询
    queryFaultOrders();
  };

  // 查询检修工单
  const queryMaintenanceOrders = async () => {
    setIsLoadingMaintenanceOrders(true);
    
    // 显示查询中的toast提示
    const loadingToast = toast.loading('正在查询检修工单，请稍候...');
    
    try {
      const lineCode = formData.line === '一号线' ? '01' : formData.line === '二号线' ? '02' : formData.line === '三号线' ? '03' : formData.line === '四号线' ? '04' : '06';
      const response = await fetch('/api/query-maintenance-orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: userInfo.userName,
          lineCode,
          station: formData.station,
          workType: activeTab
        })
      });
      
      if (response.status === 401) {
        handleSessionExpired();
        return;
      }
      
      const result = await response.json();
      
      // 关闭loading toast
      toast.dismiss(loadingToast);
      
      if (result.success) {
        setMaintenanceOrders(result.data);
        toast.success(`查询到 ${result.data.length} 条检修工单`);
      } else {
        // 检查是否为超时错误
        if (result.isTimeout) {
          toast.error(result.message);
        } else {
          toast.error(result.message || '查询检修工单失败');
        }
      }
    } catch (error) {
      console.error('查询检修工单失败:', error);
      toast.dismiss(loadingToast);
      toast.error('查询检修工单失败: ' + error.message);
    } finally {
      setIsLoadingMaintenanceOrders(false);
    }
  };

  // 查询故障工单
  const queryFaultOrders = async () => {
    setIsLoadingFaultOrders(true);
    
    // 显示查询中的toast提示
    const loadingToast = toast.loading('正在查询故障工单，请稍候...');
    
    try {
      const lineCode = formData.line === '一号线' ? '01' : formData.line === '二号线' ? '02' : formData.line === '三号线' ? '03' : formData.line === '四号线' ? '04' : '06';
      const response = await fetch('/api/query-fault-orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: userInfo.userName,
          lineCode,
          station: formData.station,
          workType: activeTab
        })
      });
      
      if (response.status === 401) {
        toast.dismiss(loadingToast);
        handleSessionExpired();
        return;
      }
      
      const result = await response.json();
      
      // 关闭loading toast
      toast.dismiss(loadingToast);
      
      if (result.success) {
        setFaultOrders(result.data);
        toast.success(`查询到 ${result.data.length} 条故障工单`);
      } else {
        // 检查是否为超时错误
        if (result.isTimeout) {
          toast.error(result.message);
        } else {
          toast.error(result.message || '查询故障工单失败');
        }
      }
    } catch (error) {
      console.error('查询故障工单失败:', error);
      toast.dismiss(loadingToast);
      toast.error('查询故障工单失败: ' + error.message);
    } finally {
      setIsLoadingFaultOrders(false);
    }
  };

  // 确认选择检修工单
  const confirmMaintenanceOrders = () => {
    if (selectedMaintenanceOrders.length === 0) {
      toast.warning('请选择至少一个检修工单');
      return;
    }
    
    const orderNumbers = selectedMaintenanceOrders.map(order => order.orderNumber).join('、');
    const orderText = `检修工单号：${orderNumbers}`;
    
    const newContent = formData.workContent ? 
      `${formData.workContent}【${orderText}】` : 
      `【${orderText}】`;
    
    handleFormChange('workContent', newContent);
    setShowMaintenanceModal(false);
    toast.success(`已添加 ${selectedMaintenanceOrders.length} 个检修工单`);
  };

  // 确认选择故障工单
  const confirmFaultOrders = () => {
    if (selectedFaultOrders.length === 0) {
      toast.warning('请选择至少一个故障工单');
      return;
    }
    
    const orderNumbers = selectedFaultOrders.map(order => order.orderNumber).join('、');
    const orderText = `故障工单号：${orderNumbers}`;
    
    const newContent = formData.workContent ? 
      `${formData.workContent}【${orderText}】` : 
      `【${orderText}】`;
    
    handleFormChange('workContent', newContent);
    setShowFaultModal(false);
    toast.success(`已添加 ${selectedFaultOrders.length} 个故障工单`);
  };

  const handleLogout = async () => {
    // 显示确认对话框
    const confirmLogout = window.confirm('确定要退出登录吗？');
    if (!confirmLogout) {
      return;
    }
    
    // 显示退出中的提示
    const logoutToast = toast.loading('正在安全退出...');
    
    try {
      // 调用服务端退出API清理会话
      if (userInfo?.userName) {
        const response = await fetch('/api/logout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            username: userInfo.userName
          })
        });
        
        const result = await response.json();
        
        if (!result.success) {
          console.warn('服务端退出失败:', result.message);
          // 即使服务端退出失败，也继续清理本地数据
        }
      }
    } catch (error) {
      console.error('调用退出API失败:', error);
      // 即使API调用失败，也继续清理本地数据
    }
    
    // 清理本地存储的用户信息
    localStorage.removeItem('userInfo');
    
    // 清理组件状态
    setUserInfo(null);
    setC2Data([]);
    setB3Data([]);
    setUserTemplates([]);
    setPublicTemplates([]);
    
    // 关闭加载提示
    toast.dismiss(logoutToast);
    
    // 跳转到登录页面并显示退出成功消息
    navigate('/login', {
      state: {
        message: '您已安全退出，请重新登录'
      }
    });
  };

  const handleEdit = async (item) => {
    if (!item.uniqueId) {
      toast.error('无法编辑：缺少唯一标识ID');
      return;
    }
    
    // 检查是否已经在编辑中或全局按钮已禁用
    if (editingItemId === item.uniqueId || isGlobalButtonsDisabled) {
      return;
    }
    
    // 设置编辑状态和全局按钮禁用
    setEditingItemId(item.uniqueId);
    setIsGlobalButtonsDisabled(true);
    const loadingToast = toast.loading('正在获取编辑数据，请稍候...');
    
    try {
      const response = await fetch('/api/edit-work-item', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: userInfo.userName,
          uniqueId: item.uniqueId,
          workType: activeTab
        })
      });
      
      if (response.status === 401) {
        handleSessionExpired();
        return;
      }
      
      const result = await response.json();
      
      if (result.success) {
        // 检查获取的数据是否为空
        const data = result.data;
        const hasValidData = data && (
          data.line || data.workDate || data.startTime || data.endTime ||
          data.workTitle || data.station || data.supervisor || data.workerCount ||
          data.workArea || data.workContent || data.safetyMeasures || data.remarks
        );
        
        if (!hasValidData) {
          toast.dismiss(loadingToast);
          toast.error('该条施工作业不存在');
          // 删除该卡片
          removeWorkItem(item.uniqueId);
          return;
        }
        
        // 保存原始数据用于比较
        setOriginalEditData(result.data);
        setEditingItem(item);
        setSelectedQuickDurations([]);
        
        // 将获取到的数据填入表单
        setFormData({
          line: result.data.line || '',
          workDate: result.data.workDate || '',
          startTime: result.data.startTime || '',
          endTime: result.data.endTime || '',
          isStartNextDay: result.data.isStartNextDay || false,
          isEndNextDay: result.data.isEndNextDay || false,
          workTitle: result.data.workTitle || '',
          station: result.data.station || '',
          supervisor: result.data.supervisor || '',
          workerCount: result.data.workerCount || '',
          workArea: result.data.workArea || '',
          workContent: result.data.workContent || '',
          safetyMeasures: result.data.safetyMeasures || '',
          remarks: result.data.remarks || ''
        });
        
        // 清除表单错误
        setFormErrors({});
        
        // 如果有线路信息，先加载对应的车站/车场数据，然后再打开模态框
        if (result.data.line) {
          try {
            // 直接获取车站/车场数据，不依赖状态更新
            const [stationsResponse, depotsResponse] = await Promise.all([
              fetch(`/api/stations/${encodeURIComponent(result.data.line)}`),
              fetch(`/api/depots/${encodeURIComponent(result.data.line)}`)
            ]);
            
            // 检查会话状态
            if (stationsResponse.status === 401 || depotsResponse.status === 401) {
              handleSessionExpired();
              return;
            }
            
            const stationsResult = await stationsResponse.json();
            const depotsResult = await depotsResponse.json();
            
            // 使用 flushSync 强制同步更新状态，确保数据已加载
             flushSync(() => {
               if (stationsResult.success) {
                 setStations(stationsResult.data);
               }
               if (depotsResult.success) {
                 setDepots(depotsResult.data);
               }
             });
          } catch (error) {
            console.error('获取车站/车场数据失败:', error);
          }
        }
        
        // 打开编辑模态框
        setShowEditModal(true);
        
        toast.dismiss(loadingToast);
        toast.success('编辑数据获取成功');
      } else {
        toast.dismiss(loadingToast);
        toast.error(result.message || '获取编辑数据失败');
      }
    } catch (error) {
      console.error('获取编辑数据失败:', error);
      toast.dismiss(loadingToast);
      toast.error('获取编辑数据失败，请稍后重试');
    } finally {
      // 重置编辑状态和全局按钮状态
      setEditingItemId(null);
      setIsGlobalButtonsDisabled(false);
    }
  };
 // 处理复制功能
  const handleCopy = async (item) => {
    if (!item.uniqueId) {
      toast.error('无法复制：缺少唯一标识ID');
      return;
    }
    
    // 检查是否已经在复制中或全局按钮已禁用
    if (copyingItemId === item.uniqueId || isGlobalButtonsDisabled) {
      return;
    }
    
    // 设置复制状态和全局按钮禁用
    setCopyingItemId(item.uniqueId);
    setIsGlobalButtonsDisabled(true);
    const loadingToast = toast.loading('正在复制并创建表单，请稍候...');
    
    try {
      const response = await fetch('/api/copy-work-item', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: userInfo.userName,
          uniqueId: item.uniqueId,
          workType: activeTab
        })
      });
      
      if (response.status === 401) {
        handleSessionExpired();
        return;
      }
      
      const result = await response.json();
      
      if (result.success) {
        // 检查获取的数据是否为空
        const data = result.data;
        const hasValidData = data && (
          data.workTitle || data.workArea || data.workContent || 
          data.safetyMeasures || data.remarks
        );
        
        if (!hasValidData) {
          toast.dismiss(loadingToast);
          toast.error('该条施工作业不存在');
          // 删除该卡片
          removeWorkItem(item.uniqueId);
          return;
        }
        
        // 获取当前日期和时间作为默认值
        const now = new Date();
        const currentHour = now.getHours();
        
        // 如果当前时间在凌晨0:00-6:00之间，默认日期为前一天
        let workDate;
        let isStartNextDay = false;
        let isEndNextDay = false;
        
        if (currentHour >= 0 && currentHour < 6) {
          // 凌晨时段，日期设为前一天，次日标记默认勾选
          const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          const year = yesterday.getFullYear();
          const month = String(yesterday.getMonth() + 1).padStart(2, '0');
          const day = String(yesterday.getDate()).padStart(2, '0');
          workDate = `${year}-${month}-${day}`;
          isStartNextDay = true;
          isEndNextDay = true;
        } else {
          // 正常时段，日期为当天
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, '0');
          const day = String(now.getDate()).padStart(2, '0');
          workDate = `${year}-${month}-${day}`;
        }
        
        const currentTime = now.toTimeString().slice(0, 5); // hh:mm格式
        
        // 计算结束时间（当前时间+30分钟）
        const endTime = new Date(now.getTime() + 30 * 60000);
        const endTimeStr = endTime.toTimeString().slice(0, 5);
        
        // 将获取到的详情数据填入表单，包括默认值
        setFormData(prev => ({
          ...prev,
          workTitle: result.data.workTitle || '',
          workArea: result.data.workArea || '',
          workContent: result.data.workContent || '',
          safetyMeasures: result.data.safetyMeasures || '',
          remarks: result.data.remarks || '',
          workDate: workDate,
          startTime: currentTime,
          endTime: endTimeStr,
          isStartNextDay: isStartNextDay,
          isEndNextDay: isEndNextDay,
          supervisor: userInfo?.name || ''
        }));
        
        // 清除相关字段的错误状态
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.workTitle;
          delete newErrors.workArea;
          delete newErrors.workContent;
          delete newErrors.safetyMeasures;
          delete newErrors.remarks;
          delete newErrors.workDate;
          delete newErrors.startTime;
          delete newErrors.endTime;
          delete newErrors.supervisor;
          return newErrors;
        });
        
        // 打开新建模态框
        setShowCreateModal(true);
        
        // 关闭加载提示
        toast.dismiss(loadingToast);
        toast.success('作业详情已复制到新建表单中');
      } else {
        // 关闭加载提示
        toast.dismiss(loadingToast);
        toast.error(result.message || '获取作业详情失败');
      }
    } catch (error) {
      console.error('复制作业失败:', error);
      // 关闭加载提示
      toast.dismiss(loadingToast);
      toast.error('复制作业失败，请稍后重试');
    } finally {
      // 重置复制状态和全局按钮状态
      setCopyingItemId(null);
      setIsGlobalButtonsDisabled(false);
    }
  };

  const handleDelete = async (item) => {
    // 检查是否有唯一标识ID
    if (!item.uniqueId) {
      toast.error('该条记录无法删除：缺少唯一标识');
      return;
    }
    
    // 检查是否已经在删除中或全局按钮已禁用
    if (deletingItemId === item.uniqueId || isGlobalButtonsDisabled) {
      return;
    }
    
    if (window.confirm(`确定要删除施工作业"${item.workCode}"吗？`)) {
      // 设置正在删除的项目ID和全局按钮禁用
      setDeletingItemId(item.uniqueId);
      setIsGlobalButtonsDisabled(true);
      const loadingToast = toast.loading('正在删除，请稍候...');
      
      try {
        const response = await fetch('/api/delete-work-item', {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username: userInfo.userName,
            uniqueId: item.uniqueId,
            workType: activeTab
          })
        });
        
        // 检查会话状态
        if (response.status === 401) {
          toast.dismiss(loadingToast);
          handleSessionExpired();
          return;
        }
        
        const result = await response.json();
        
        // 关闭加载提示
        toast.dismiss(loadingToast);
        
        if (response.ok && result.success) {
          toast.success('删除成功');
          // 立即从前端数据中移除该卡片
          if (activeTab === 'C2') {
            setC2Data(prevData => 
              prevData.filter(dataItem => dataItem.uniqueId !== item.uniqueId)
            );
          } else {
            setB3Data(prevData => 
              prevData.filter(dataItem => dataItem.uniqueId !== item.uniqueId)
            );
          }
        } else {
          // 检查是否是未找到作业的情况
          if ((result.message && result.message.includes('未找到')) || (result.message && result.message.includes('不存在'))) {
            toast.error('该条施工作业不存在');
            // 删除该卡片
            removeWorkItem(item.uniqueId);
          } else {
            toast.error(result.message || '删除失败');
          }
        }
      } catch (error) {
        console.error('删除失败:', error);
        toast.dismiss(loadingToast);
        toast.error('删除失败: ' + error.message);
      } finally {
        // 清除正在删除的项目ID和重置全局按钮状态
        setDeletingItemId(null);
        setIsGlobalButtonsDisabled(false);
      }
    }
  };

  const handleCreate = () => {
    setShowCreateModal(true);
    
    // 获取当前日期和时间（使用本地时间）
    const now = new Date();
    const currentHour = now.getHours();
    
    // 如果当前时间在凌晨0:00-6:00之间，默认日期为前一天
    let workDate;
    let isStartNextDay = false;
    let isEndNextDay = false;
    
    if (currentHour >= 0 && currentHour < 6) {
      // 凌晨时段，日期设为前一天，次日标记默认勾选
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      // 使用本地时间格式化日期
      const year = yesterday.getFullYear();
      const month = String(yesterday.getMonth() + 1).padStart(2, '0');
      const day = String(yesterday.getDate()).padStart(2, '0');
      workDate = `${year}-${month}-${day}`;
      isStartNextDay = true;
      isEndNextDay = true;
    } else {
      // 正常时段，日期为当天
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      workDate = `${year}-${month}-${day}`;
    }
    
    const currentTime = now.toTimeString().slice(0, 5); // hh:mm格式
    
    // 计算结束时间（当前时间+30分钟）
    const endTime = new Date(now.getTime() + 30 * 60000);
    const endTimeStr = endTime.toTimeString().slice(0, 5);
    
    // 重置表单数据并设置默认值
    setSelectedQuickDurations([]);
    setFormData({
      line: '',
      workDate: workDate,
      startTime: currentTime,
      endTime: endTimeStr,
      isStartNextDay: isStartNextDay,
      isEndNextDay: isEndNextDay,
      workTitle: '',
      station: '',
      supervisor: userInfo?.name || '', // 默认为当前用户姓名
      workerCount: '',
      workArea: '',
      workContent: '',
      safetyMeasures: '',
      remarks: ''
    });
    setFormErrors({});
  };

  // 处理筛选条件变化
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 处理快速时间间距选择
  const handleQuickDuration = (durationMinutes) => {
    const nextSelectedDurations = selectedQuickDurations.includes(durationMinutes)
      ? selectedQuickDurations.filter(item => item !== durationMinutes)
      : [...selectedQuickDurations, durationMinutes].sort((a, b) => a - b);

    setSelectedQuickDurations(nextSelectedDurations);

    if (nextSelectedDurations.length === 0) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.startTime;
        delete newErrors.endTime;
        return newErrors;
      });
      return;
    }

    const baseStart = new Date();
    if (selectedQuickDurations.length > 0 && formData.startTime) {
      const [hours, minutes] = formData.startTime.split(':').map(Number);
      if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
        baseStart.setHours(hours, minutes, 0, 0);
      } else {
        baseStart.setSeconds(0, 0);
      }
    } else {
      baseStart.setSeconds(0, 0);
    }

    const totalDurationMinutes = nextSelectedDurations.reduce((total, current) => total + current, 0);
    const startHours = baseStart.getHours();
    const startMinutes = baseStart.getMinutes();
    const startTimeStr = `${String(startHours).padStart(2, '0')}:${String(startMinutes).padStart(2, '0')}`;

    const end = new Date(baseStart.getTime() + totalDurationMinutes * 60000);
    const endHours = end.getHours();
    const endMinutes = end.getMinutes();
    const endTimeStr = `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
    
    // 判断次日勾选
    // 0-6点视为次日
    const isStartNextDay = startHours >= 0 && startHours < 6;
    const isEndNextDay = endHours >= 0 && endHours < 6;
    
    // 特殊情况：如果当前时间是深夜（例如23:00），加了时间后变成凌晨（01:00），虽然01:00在0-6之间，但相对于23:00确实是次日
    // 如果开始时间是23:00（非次日），结束时间是01:00（次日），逻辑正确
    // 如果开始时间是01:00（次日），结束时间是03:00（次日），逻辑正确
    
    setFormData(prev => ({
      ...prev,
      startTime: startTimeStr,
      endTime: endTimeStr,
      isStartNextDay: isStartNextDay,
      isEndNextDay: isEndNextDay
    }));
    
    // 清除相关错误
    setFormErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.startTime;
      delete newErrors.endTime;
      return newErrors;
    });
  };

  // 处理开始时间取整
  const handleQuickRound = (intervalMinutes) => {
    const now = new Date();
    const currentMinutes = now.getMinutes();
    
    // 计算下一个整点分钟
    // 例如：当前16分，间隔10分 -> ceil(16/10)*10 = 20分
    // 当前20分，间隔10分 -> 20分（保持不变还是下一级？用户说"当前为9:16，则开始时间就设置为9:20"。如果是9:20，通常理解是保持不变或下一个。这里假设向上取整，如果正好是整点则不变）
    let targetMinutes = Math.ceil(currentMinutes / intervalMinutes) * intervalMinutes;
    
    // 如果计算结果等于当前分钟数（即当前已经是整点），为了用户体验，可以不做处理，或者跳到下一个。
    // 这里保持向上取整的逻辑，如果已经是整点就不变
    
    const newStart = new Date(now);
    newStart.setMinutes(targetMinutes);
    newStart.setSeconds(0);
    
    const startHours = newStart.getHours();
    const startMinutes = newStart.getMinutes();
    const startTimeStr = `${String(startHours).padStart(2, '0')}:${String(startMinutes).padStart(2, '0')}`;
    
    // 判断次日
    const isStartNextDay = startHours >= 0 && startHours < 6;
    
    // 保持原来的时长不变
    // 先计算原来的时长
    let originalDuration = 0;
    if (formData.startTime && formData.endTime) {
      const [sh, sm] = formData.startTime.split(':').map(Number);
      const [eh, em] = formData.endTime.split(':').map(Number);
      
      let startTotal = sh * 60 + sm;
      if (formData.isStartNextDay) startTotal += 24 * 60;
      
      let endTotal = eh * 60 + em;
      if (formData.isEndNextDay) endTotal += 24 * 60;
      
      originalDuration = endTotal - startTotal;
    }
    
    // 如果原时长无效（<=0），默认设为30分钟
    if (originalDuration <= 0) originalDuration = 30;
    
    // 计算新的结束时间
    const newEnd = new Date(newStart.getTime() + originalDuration * 60000);
    const endHours = newEnd.getHours();
    const endMinutes = newEnd.getMinutes();
    const endTimeStr = `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
    const isEndNextDay = endHours >= 0 && endHours < 6;
    
    setFormData(prev => ({
      ...prev,
      startTime: startTimeStr,
      endTime: endTimeStr,
      isStartNextDay: isStartNextDay,
      isEndNextDay: isEndNextDay
    }));
    
    // 清除错误
    setFormErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.startTime;
      delete newErrors.endTime;
      return newErrors;
    });
  };

  // 处理表单字段变化
  const handleFormChange = (field, value) => {
    if (['startTime', 'endTime', 'isStartNextDay', 'isEndNextDay'].includes(field)) {
      setSelectedQuickDurations([]);
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // 清除对应字段的错误
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // 表单验证
  const validateForm = () => {
    const errors = {};
    
    if (!formData.line) errors.line = '请选择线路';
    if (!formData.workDate) {
      errors.workDate = '请输入作业日期';
    } else {
      // 验证日期格式 yyyy-mm-dd
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(formData.workDate)) {
        errors.workDate = '日期格式错误，请使用yyyy-mm-dd格式';
      } else {
        // 验证日期是否有效
        const date = new Date(formData.workDate);
        const [year, month, day] = formData.workDate.split('-').map(Number);
        if (date.getFullYear() !== year || date.getMonth() + 1 !== month || date.getDate() !== day) {
          errors.workDate = '请输入有效的日期';
        }
      }
    }
    
    if (!formData.startTime) {
      errors.startTime = '请输入开始时间';
    } else {
      // 验证时间格式 hh:mm
      const timeRegex = /^\d{2}:\d{2}$/;
      if (!timeRegex.test(formData.startTime)) {
        errors.startTime = '时间格式错误，请使用hh:mm格式';
      } else {
        const [hours, minutes] = formData.startTime.split(':').map(Number);
        if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
          errors.startTime = '请输入有效的时间';
        }
      }
    }
    
    if (!formData.endTime) {
      errors.endTime = '请输入结束时间';
    } else {
      // 验证时间格式 hh:mm
      const timeRegex = /^\d{2}:\d{2}$/;
      if (!timeRegex.test(formData.endTime)) {
        errors.endTime = '时间格式错误，请使用hh:mm格式';
      } else {
        const [hours, minutes] = formData.endTime.split(':').map(Number);
        if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
          errors.endTime = '请输入有效的时间';
        }
      }
    }
    
    if (!formData.workTitle) errors.workTitle = '请输入作业标题';
    if (!formData.station) errors.station = activeTab === 'B3' ? '请选择作业车场' : '请选择作业车站';
    if (!formData.supervisor) errors.supervisor = '请输入施工负责人';
    if (!formData.workerCount) errors.workerCount = '请输入作业人数';
    if (!formData.workArea) errors.workArea = '请输入作业区域';
    if (!formData.workContent) errors.workContent = '请输入作业内容';
    if (!formData.safetyMeasures) errors.safetyMeasures = '请输入安全防护措施';
    if (!formData.remarks) errors.remarks = '请输入备注';
    
    // 验证作业人数是否为正整数
    if (formData.workerCount && (!/^\d+$/.test(formData.workerCount) || parseInt(formData.workerCount) <= 0)) {
      errors.workerCount = '作业人数必须为正整数';
    }
    
    // 验证次日时间范围
    if (formData.isStartNextDay && formData.startTime && !errors.startTime) {
      const [startHours] = formData.startTime.split(':').map(Number);
      if (startHours < 0 || startHours >= 6) {
        errors.startTime = '勾选次日的开始时间必须在0:00-5:59之间';
      }
    }
    
    if (formData.isEndNextDay && formData.endTime && !errors.endTime) {
      const [endHours] = formData.endTime.split(':').map(Number);
      if (endHours < 0 || endHours >= 6) {
        errors.endTime = '勾选次日的结束时间必须在0:00-5:59之间';
      }
    }
    
    // 验证时间逻辑（只有在时间格式正确的情况下才验证逻辑）
    if (formData.startTime && formData.endTime && !errors.startTime && !errors.endTime) {
      const startTime = formData.startTime;
      const endTime = formData.endTime;
      const isStartNextDay = formData.isStartNextDay;
      const isEndNextDay = formData.isEndNextDay;
      
      // 将时间转换为分钟数进行比较
      const [startHours, startMinutes] = startTime.split(':').map(Number);
      const [endHours, endMinutes] = endTime.split(':').map(Number);
      const startTotalMinutes = startHours * 60 + startMinutes;
      const endTotalMinutes = endHours * 60 + endMinutes;
      
      // 根据次日标记调整时间
      let adjustedStartMinutes = startTotalMinutes;
      let adjustedEndMinutes = endTotalMinutes;
      
      if (isStartNextDay) {
        adjustedStartMinutes += 24 * 60; // 加一天
      }
      if (isEndNextDay) {
        adjustedEndMinutes += 24 * 60; // 加一天
      }
      
      // 验证结束时间必须晚于开始时间
      if (adjustedEndMinutes <= adjustedStartMinutes) {
        if (isStartNextDay && !isEndNextDay) {
          errors.startTime = '开始时间标记为次日时，结束时间也应为次日或更晚';
        } else {
          errors.endTime = '结束时间必须晚于开始时间';
        }
      }
      
      // 验证时间跨度不能超过24小时
      if (adjustedEndMinutes - adjustedStartMinutes > 24 * 60) {
        errors.endTime = '作业时间跨度不能超过24小时';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 检测字段变更
  const getChangedFields = () => {
    if (!originalEditData) return {};
    
    const changedFields = {};
    
    // 比较所有字段
    if (formData.startTime !== originalEditData.startTime) {
      changedFields.startTime = formData.startTime;
    }
    if (formData.endTime !== originalEditData.endTime) {
      changedFields.endTime = formData.endTime;
    }
    if (formData.isStartNextDay !== originalEditData.isStartNextDay) {
      changedFields.isStartNextDay = formData.isStartNextDay;
    }
    if (formData.isEndNextDay !== originalEditData.isEndNextDay) {
      changedFields.isEndNextDay = formData.isEndNextDay;
    }
    if (formData.station !== originalEditData.station) {
      changedFields.station = formData.station;
    }
    if (formData.supervisor !== originalEditData.supervisor) {
      changedFields.supervisor = formData.supervisor;
    }
    if (formData.workerCount !== originalEditData.workerCount) {
      changedFields.workerCount = formData.workerCount;
    }
    if (formData.workTitle !== originalEditData.workTitle) {
      changedFields.workTitle = formData.workTitle;
    }
    if (formData.workArea !== originalEditData.workArea) {
      changedFields.workArea = formData.workArea;
    }
    if (formData.workContent !== originalEditData.workContent) {
      changedFields.workContent = formData.workContent;
    }
    if (formData.safetyMeasures !== originalEditData.safetyMeasures) {
      changedFields.safetyMeasures = formData.safetyMeasures;
    }
    if (formData.remarks !== originalEditData.remarks) {
      changedFields.remarks = formData.remarks;
    }
    
    return changedFields;
  };

  // 处理编辑提交
  const handleEditSubmit = async () => {
    if (!validateForm()) {
      toast.error('请检查表单填写是否正确');
      return;
    }
    
    const changedFields = getChangedFields();
    
    if (Object.keys(changedFields).length === 0) {
      toast.info('没有检测到任何修改');
      return;
    }
    
    if (isEditSubmitting) {
      return; // 防止重复提交
    }
    
    setIsEditSubmitting(true);
    
    try {
      // 显示提交中状态
      const loadingToast = toast.loading('正在保存修改，请稍候...');
      
      // 构造提交数据
      const submitData = {
        ...formData,
        username: userInfo.userName,
        uniqueId: editingItem.uniqueId,
        workType: activeTab,
        originalData: originalEditData,
        changedFields: changedFields
      };
      
      const response = await fetch('/api/submit-edit-work-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData)
      });
      
      // 检查会话状态
      if (response.status === 401) {
        toast.dismiss(loadingToast);
        handleSessionExpired();
        return;
      }
      
      const result = await response.json();
      
      // 关闭加载提示
      toast.dismiss(loadingToast);
      
      if (response.ok) {
        if (result.success) {
           toast.success('施工作业编辑成功');
           setShowEditModal(false);
           setEditingItem(null);
           setOriginalEditData(null);
        } else if (result.needSelection) {
          // 处理多个施工负责人候选的情况
          setSupervisorCandidates(result.candidates);
          setPendingFormData({
            ...formData,
            username: userInfo.userName,
            uniqueId: editingItem.uniqueId,
            workType: activeTab,
            originalData: originalEditData,
            changedFields: changedFields
          });
          setShowSupervisorModal(true);
          // 注意：这里不重置 isEditSubmitting，保持提交按钮禁用状态直到用户完成选择
          return; // 提前返回，不执行finally中的重置
        } else {
          toast.error(result.message || '编辑失败');
        }
      } else {
        toast.error(result.message || '编辑失败');
      }
    } catch (error) {
      console.error('编辑提交错误:', error);
      toast.error('编辑失败: ' + error.message);
    } finally {
      setIsEditSubmitting(false);
    }
  };

  // 处理表单提交
  const handleFormSubmit = async () => {
    if (!validateForm()) {
      toast.error('请检查表单填写是否正确');
      return;
    }
    
    if (isSubmitting) {
      return; // 防止重复提交
    }
    
    setIsSubmitting(true);
    
    try {
      // 显示提交中状态
      const loadingToast = toast.loading('正在提交表单，请稍候...');
      
      // 构造提交数据
      const submitData = {
        ...formData,
        workType: activeTab, // C2 或 B3
        username: userInfo.userName // 添加用户名参数
      };
      
      const response = await fetch('/api/submit-work-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData)
      });
      
      // 检查会话状态
      if (response.status === 401) {
        toast.dismiss(loadingToast);
          handleSessionExpired();
        return;
      }
      
      const result = await response.json();
      
      // 关闭加载提示
      toast.dismiss(loadingToast);
      
      if (response.ok) {
         if (result.success) {
           toast.success('施工作业表单提交成功');
           setShowCreateModal(false);
           setIsSubmitting(false);
         } else if (result.needSelection) {
           // 处理多个施工负责人候选的情况
           setSupervisorCandidates(result.candidates);
           setPendingFormData(submitData);
           setShowSupervisorModal(true);
           // 注意：这里不重置 isSubmitting，保持提交按钮禁用状态直到用户完成选择
           return; // 提前返回，不执行finally中的重置
         } else {
           toast.error(result.message || '提交失败');
           setIsSubmitting(false);
         }
      } else {
        throw new Error(result.message || '网络请求失败');
      }
    } catch (error) {
      console.error('表单提交错误:', error);
      toast.error('提交失败: ' + error.message);
      setIsSubmitting(false);
    }
  };

  // 处理施工负责人选择
   const handleSupervisorSelect = async (selectedSupervisor) => {
     // 显示提交中状态
     const loadingToast = toast.loading('正在提交表单，请稍候...');
     
     try {
       const updatedFormData = {
         ...pendingFormData,
         selectedSupervisorIndex: selectedSupervisor.index
       };
       
       // 判断是新建还是编辑模式
       const isEditMode = showEditModal && editingItem;
       
       // 先设置提交状态，确保组件保持禁用
       if (isEditMode) {
         setIsEditSubmitting(true);
       } else {
         setIsSubmitting(true);
       }
       
       // 关闭施工负责人选择模态框
       setShowSupervisorModal(false);
       
       const apiUrl = isEditMode ? '/api/submit-edit-work-form-with-selection' : '/api/submit-work-form-with-selection';
       
       const response = await fetch(apiUrl, {
         method: 'POST',
         headers: {
           'Content-Type': 'application/json',
         },
         body: JSON.stringify(updatedFormData)
       });
       
       // 检查会话状态
       if (response.status === 401) {
         toast.dismiss(loadingToast);
         handleSessionExpired();
         return;
       }
       
       const result = await response.json();
       
       // 关闭加载提示
       toast.dismiss(loadingToast);
       
       if (response.ok && result.success) {
         if (isEditMode) {
           toast.success('施工作业编辑成功');
           setShowEditModal(false);
           setEditingItem(null);
           setOriginalEditData(null);
         } else {
           toast.success('施工作业表单提交成功');
           setShowCreateModal(false);
         }
         setPendingFormData(null);
         setSupervisorCandidates([]);
       } else {
         toast.error(result.message || '提交失败');
         // 如果提交失败，重新显示施工负责人选择模态框
         setShowSupervisorModal(true);
       }
     } catch (error) {
       console.error('表单提交错误:', error);
       toast.dismiss(loadingToast);
       toast.error('提交失败: ' + error.message);
       // 如果提交失败，重新显示施工负责人选择模态框
       setShowSupervisorModal(true);
     } finally {
       // 无论成功还是失败，都重置提交状态
       if (showEditModal) {
         setIsEditSubmitting(false);
       } else {
         setIsSubmitting(false);
       }
     }
   };

  // 重置筛选条件
  const handleResetFilters = () => {
    setFilters({
      line: '',
      workCode: '',
      department: '',
      station: '',
      content: '',
      area: '',
      supervisor: '',
      status: ''
    });
    setSelectedLine('');
    setStations([]);
    setDepots([]);
    setShowMyData(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case '待请点':
        return 'status-pending';
      case '已请点':
        return 'status-progress';
      case '已完成':
        return 'status-completed';
      default:
        return 'status-default';
    }
  };

  if (!userInfo) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>加载中...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      {/* 头部导航 */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <div className="user-info">
              <FiUser className="user-icon" />
              <span className="user-name">{userInfo.name}</span>
            </div>
          </div>
          
          <div className="header-center">
            <h1 className="header-title">施工作业台账</h1>
          </div>
          
          <div className="header-right">
            {userInfo?.isAdmin && (
              <button className="admin-entry-btn" onClick={() => navigate('/admin/manage')}>
                <FiShield className="admin-entry-icon" />
                <span className="admin-entry-text">管理后台</span>
              </button>
            )}
            <button className="feedback-btn" onClick={() => setShowFeedbackModal(true)} title="意见建议">
              <FiMessageSquare className="feedback-icon" />
              <span className="feedback-text">意见建议</span>
            </button>
            <button className="logout-btn" onClick={handleLogout}>
              <FiLogOut className="logout-icon" />
              <span className="logout-text">退出</span>
            </button>
          </div>
        </div>
      </header>

      {/* 主要内容区域 */}
      <main className="dashboard-main">
        <div className="content-container">


 
           {/* 筛选模态框 */}
          {showFeedbackModal && (
            <div className="save-template-modal-overlay">
              <div className="save-template-modal feedback-modal">
                <div className="modal-header">
                  <h3>意见建议</h3>
                  <button className="close-btn" onClick={closeFeedbackModal} disabled={isFeedbackSubmitting}>×</button>
                </div>
                <div className="modal-content">
                  <div className="form-item">
                    <label>反馈类型 <span className="required">*</span></label>
                    <select
                      value={feedbackForm.type}
                      onChange={(e) => handleFeedbackFormChange('type', e.target.value)}
                      disabled={isFeedbackSubmitting}
                    >
                      <option value="功能建议">功能建议</option>
                      <option value="问题反馈">问题反馈</option>
                      <option value="体验优化">体验优化</option>
                      <option value="其他">其他</option>
                    </select>
                  </div>
                  <div className="form-item">
                    <label>意见内容 <span className="required">*</span></label>
                    <textarea
                      className="feedback-textarea"
                      placeholder="欢迎填写你的使用感受、功能建议或遇到的问题"
                      value={feedbackForm.content}
                      onChange={(e) => handleFeedbackFormChange('content', e.target.value.slice(0, 500))}
                      rows="6"
                      disabled={isFeedbackSubmitting}
                    />
                  </div>
                  <div className="feedback-meta">
                    <span>提交人：{userInfo.name}</span>
                    <span>{feedbackForm.content.length}/500</span>
                  </div>
                  <div className="modal-actions">
                    <button
                      className="save-btn"
                      onClick={submitFeedback}
                      disabled={isFeedbackSubmitting}
                    >
                      {isFeedbackSubmitting ? '提交中...' : '提交'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 筛选模态框 */}
           {showFilterModal && (
             <div className="filter-modal-overlay" onClick={() => setShowFilterModal(false)}>
               <div className="filter-modal" onClick={(e) => e.stopPropagation()}>
                 <div className="filter-modal-header">
                   <h3>筛选条件</h3>
                   <button className="close-btn" onClick={() => setShowFilterModal(false)}>×</button>
                 </div>
                 <div className="filter-modal-content">
                   <div className="filter-modal-actions">
                     <button 
                       className={`my-data-btn ${showMyData ? 'active' : ''}`}
                       onClick={() => setShowMyData(!showMyData)}
                     >
                       只显示我的数据
                     </button>
                     <button className="reset-btn" onClick={handleResetFilters}>
                       重置筛选
                     </button>
                   </div>
                   <div className="filter-grid">
                     <div className="filter-item">
                       <label>线路</label>
                       <select
                         value={selectedLine}
                         onChange={(e) => handleLineChange(e.target.value)}
                       >
                         <option value="">请选择线路</option>
                         {lines.map(line => (
                           <option key={line} value={line}>{line}</option>
                         ))}
                       </select>
                     </div>
                     
                     <div className="filter-item">
                       <label>{activeTab === 'B3' ? '作业车场' : '作业车站'}</label>
                       <select
                         value={filters.station}
                         onChange={(e) => handleFilterChange('station', e.target.value)}
                         disabled={!selectedLine}
                       >
                         <option value="">{activeTab === 'B3' ? '请选择车场' : '请选择车站'}</option>
                         {activeTab === 'C2' ? (
                           stations.map(station => (
                             <option key={station} value={station}>{station}</option>
                           ))
                         ) : (
                           depots.map(depot => (
                             <option key={depot} value={depot}>{depot}</option>
                           ))
                         )}
                       </select>
                     </div>
                     
                     <div className="filter-item">
                       <label>作业代码</label>
                       <input
                         type="text"
                         placeholder="请输入作业代码"
                         value={filters.workCode}
                         onChange={(e) => handleFilterChange('workCode', e.target.value)}
                       />
                     </div>
                     
                     <div className="filter-item">
                       <label>作业部门</label>
                       <input
                         type="text"
                         placeholder="请输入作业部门"
                         value={filters.department}
                         onChange={(e) => handleFilterChange('department', e.target.value)}
                       />
                     </div>
                     
                     <div className="filter-item">
                       <label>作业内容</label>
                       <input
                         type="text"
                         placeholder="请输入作业内容"
                         value={filters.content}
                         onChange={(e) => handleFilterChange('content', e.target.value)}
                       />
                     </div>
                     
                     <div className="filter-item">
                       <label>作业区域</label>
                       <input
                         type="text"
                         placeholder="请输入作业区域"
                         value={filters.area}
                         onChange={(e) => handleFilterChange('area', e.target.value)}
                       />
                     </div>
                     
                     <div className="filter-item">
                       <label>施工负责人</label>
                       <input
                         type="text"
                         placeholder="请输入施工负责人"
                         value={filters.supervisor}
                         onChange={(e) => handleFilterChange('supervisor', e.target.value)}
                       />
                     </div>
                     
                     <div className="filter-item">
                       <label>业务状态</label>
                       <select
                         value={filters.status}
                         onChange={(e) => handleFilterChange('status', e.target.value)}
                       >
                         <option value="">全部状态</option>
                         <option value="待请点">待请点</option>
                         <option value="已请点">已请点</option>
                         <option value="已完成">已完成</option>
                       </select>
                     </div>
                   </div>
                 </div>
               </div>
             </div>
           )}

           {/* 施工负责人选择模态框 */}
           {showSupervisorModal && (
             <div className="supervisor-modal-overlay">
               <div className="supervisor-modal">
                 <div className="modal-header">
                   <h3>选择施工负责人</h3>
                   <button className="close-btn" onClick={() => {
                     setShowSupervisorModal(false);
                     setPendingFormData(null);
                     setSupervisorCandidates([]);
                     // 根据当前模式重置对应的提交状态
                     if (showEditModal) {
                       setIsEditSubmitting(false);
                     } else {
                       setIsSubmitting(false);
                     }
                   }}>×</button>
                 </div>
                 <div className="modal-content">
                   <p className="supervisor-hint">系统检测到多个可能的施工负责人，请选择正确的负责人：</p>
                    <div className="supervisor-list">
                      {supervisorCandidates.map((candidate, index) => (
                        <div key={index} className="supervisor-item" onClick={() => handleSupervisorSelect(candidate)}>
                          <div className="supervisor-info">
                            <div className="supervisor-name">姓名：{candidate.name}</div>
                            <div className="supervisor-phone">联系方式：{candidate.phone}</div>
                            <div className="supervisor-dept">作业部门：{candidate.department}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                 </div>
               </div>
             </div>
           )}

           {/* 新建施工作业模态框 */}
           {showCreateModal && (
             <div className="create-modal-overlay">
               <div className="create-modal">
                 <div className="modal-header">
                   <div className="header-title">
                     <h3>新建{activeTab === 'C2' ? 'C2-1' : 'B3'}类施工作业</h3>
                   </div>
                   <div className="header-close">
                     <button 
                       className="close-btn" 
                       onClick={() => setShowCreateModal(false)}
                       disabled={isSubmitting || showSupervisorModal}
                     >
                       ×
                     </button>
                   </div>
                 </div>
                 <div className="create-modal-header-actions">
                   <button 
                     className="template-btn"
                     onClick={openUserTemplatesModal}
                     title="使用模板"
                   >
                     <FiBookmark /> 使用模板
                   </button>
                   <button 
                     className="save-template-btn"
                     onClick={() => setShowSaveTemplateModal(true)}
                     title="保存为模板"
                   >
                     <FiSave /> 保存模板
                   </button>
                 </div>
                 <div className="modal-content">
                   <div className="form-grid">
                     <div className="form-item">
                       <label>线路 <span className="required">*</span></label>
                       <select
                         value={formData.line}
                         onChange={(e) => {
                           handleFormChange('line', e.target.value);
                           if (e.target.value) {
                             fetchStationsAndDepots(e.target.value);
                           }
                         }}
                         className={formErrors.line ? 'error' : ''}
                         disabled={isSubmitting || showSupervisorModal}
                       >
                         <option value="">请选择线路</option>
                         {lines.map(line => (
                           <option key={line} value={line}>{line}</option>
                         ))}
                       </select>
                       {formErrors.line && <span className="error-text">{formErrors.line}</span>}
                     </div>

                     <div className="form-item">
                       <label>{activeTab === 'B3' ? '作业车场' : '作业车站'} <span className="required">*</span></label>
                       <select
                         value={formData.station}
                         onChange={(e) => handleFormChange('station', e.target.value)}
                         disabled={!formData.line || isSubmitting || showSupervisorModal}
                         className={formErrors.station ? 'error' : ''}
                       >
                         <option value="">{activeTab === 'B3' ? '请选择车场' : '请选择车站'}</option>
                         {activeTab === 'C2' ? (
                           stations.map(station => (
                             <option key={station} value={station}>{station}</option>
                           ))
                         ) : (
                           depots.map(depot => (
                             <option key={depot} value={depot}>{depot}</option>
                           ))
                         )}
                       </select>
                       {formErrors.station && <span className="error-text">{formErrors.station}</span>}
                     </div>

                     <div className="form-item">
                       <label>作业标题 <span className="required">*</span></label>
                       <input
                         type="text"
                         placeholder="请输入作业标题"
                         value={formData.workTitle}
                         onChange={(e) => handleFormChange('workTitle', e.target.value)}
                         className={formErrors.workTitle ? 'error' : ''}
                         disabled={isSubmitting || showSupervisorModal}
                       />
                       {formErrors.workTitle && <span className="error-text">{formErrors.workTitle}</span>}
                     </div>

                     <div className="form-item">
                       <label>作业日期 <span className="required">*</span></label>
                       <input
                         type="text"
                         placeholder="yyyy-mm-dd"
                         value={formData.workDate}
                         onChange={(e) => handleFormChange('workDate', e.target.value)}
                         className={formErrors.workDate ? 'error' : ''}
                         disabled={isSubmitting || showSupervisorModal}
                       />
                       {formErrors.workDate && <span className="error-text">{formErrors.workDate}</span>}
                     </div>

                     <div className="form-item time-item">
                       <label>开始时间 <span className="required">*</span></label>
                       <div className="time-input-group">
                         <input
                           type="text"
                           placeholder="hh:mm"
                           value={formData.startTime}
                           onChange={(e) => handleFormChange('startTime', e.target.value)}
                           className={formErrors.startTime ? 'error' : ''}
                           disabled={isSubmitting || showSupervisorModal}
                         />
                         <label className="checkbox-label">
                           <input
                             type="checkbox"
                             checked={formData.isStartNextDay}
                             onChange={(e) => handleFormChange('isStartNextDay', e.target.checked)}
                             disabled={isSubmitting || showSupervisorModal}
                           />
                           次日
                         </label>
                       </div>
                       {formErrors.startTime && <span className="error-text">{formErrors.startTime}</span>}
                     </div>

                     <div className="form-item time-item">
                       <label>结束时间 <span className="required">*</span></label>
                       <div className="time-input-group">
                         <input
                           type="text"
                           placeholder="hh:mm"
                           value={formData.endTime}
                           onChange={(e) => handleFormChange('endTime', e.target.value)}
                           className={formErrors.endTime ? 'error' : ''}
                           disabled={isSubmitting || showSupervisorModal}
                         />
                         <label className="checkbox-label">
                           <input
                             type="checkbox"
                             checked={formData.isEndNextDay}
                             onChange={(e) => handleFormChange('isEndNextDay', e.target.checked)}
                             disabled={isSubmitting || showSupervisorModal}
                           />
                           次日
                         </label>
                       </div>
                       {formErrors.endTime && <span className="error-text">{formErrors.endTime}</span>}
                     </div>

                     <div className="form-item quick-actions-container full-width">
                       <div className="quick-actions-row">
                         <span className="quick-label">作业时长快速选择（可多选）：</span>
                        {selectedDurationText && (
                          <span className="quick-duration-summary">已选总时长：{selectedDurationText}</span>
                        )}
                        {QUICK_DURATION_OPTIONS.map(option => (
                          <button
                            key={option.value}
                            type="button"
                            className={`quick-btn ${selectedQuickDurations.includes(option.value) ? 'selected' : ''}`}
                            onClick={() => handleQuickDuration(option.value)}
                            disabled={isSubmitting || showSupervisorModal}
                          >
                            {option.label}
                          </button>
                        ))}
                       </div>
                       <div className="quick-actions-row">
                         <span className="quick-label">开始时间向后取整：</span>
                        {QUICK_ROUND_OPTIONS.map(option => (
                          <button
                            key={option.value}
                            type="button"
                            className="quick-btn"
                            onClick={() => handleQuickRound(option.value)}
                            disabled={isSubmitting || showSupervisorModal}
                          >
                            {option.label}
                          </button>
                        ))}
                       </div>
                     </div>

                     <div className="form-item">
                       <label>施工负责人 <span className="required">*</span></label>
                       <input
                         type="text"
                         placeholder="请输入施工负责人"
                         value={formData.supervisor}
                         onChange={(e) => handleFormChange('supervisor', e.target.value)}
                         className={formErrors.supervisor ? 'error' : ''}
                         disabled={isSubmitting || showSupervisorModal}
                       />
                       {formErrors.supervisor && <span className="error-text">{formErrors.supervisor}</span>}
                     </div>

                     <div className="form-item">
                       <label>作业人数 <span className="required">*</span></label>
                       <input
                         type="number"
                         placeholder="请输入作业人数"
                         value={formData.workerCount}
                         onChange={(e) => handleFormChange('workerCount', e.target.value)}
                         min="1"
                         className={formErrors.workerCount ? 'error' : ''}
                         disabled={isSubmitting || showSupervisorModal}
                       />
                       {formErrors.workerCount && <span className="error-text">{formErrors.workerCount}</span>}
                     </div>

                     <div className="form-item full-width">
                       <label>作业区域 <span className="required">*</span></label>
                       <textarea
                         placeholder="请输入作业区域"
                         value={formData.workArea}
                         onChange={(e) => handleFormChange('workArea', e.target.value)}
                         rows="5"
                         className={formErrors.workArea ? 'error' : ''}
                         disabled={isSubmitting || showSupervisorModal}
                       />
                       {formErrors.workArea && <span className="error-text">{formErrors.workArea}</span>}
                     </div>

                     <div className="form-item full-width">
                       <label>作业内容 <span className="required">*</span></label>
                       <div className="work-content-container">
                         <textarea
                           placeholder="请输入作业内容"
                           value={formData.workContent}
                           onChange={(e) => handleFormChange('workContent', e.target.value)}
                           rows="5"
                           className={formErrors.workContent ? 'error' : ''}
                           disabled={isSubmitting || showSupervisorModal}
                         />
                         <div className="work-content-buttons">
                           <button
                             type="button"
                             className="order-btn maintenance-btn"
                             onClick={openMaintenanceModal}
                             disabled={!formData.line || !formData.station || isSubmitting || showSupervisorModal}
                             title={!formData.line ? '请先选择线路' : !formData.station ? '请先选择作业车站' : '添加检修工单号'}
                           >
                             添加检修工单号
                           </button>
                           <button
                             type="button"
                             className="order-btn fault-btn"
                             onClick={openFaultModal}
                             disabled={!formData.line || !formData.station || isSubmitting || showSupervisorModal}
                             title={!formData.line ? '请先选择线路' : !formData.station ? '请先选择作业车站' : '添加故障工单号'}
                           >
                             添加故障工单号
                           </button>
                         </div>
                       </div>
                       {formErrors.workContent && <span className="error-text">{formErrors.workContent}</span>}
                     </div>

                     <div className="form-item full-width">
                       <label>安全防护措施 <span className="required">*</span></label>
                       <textarea
                         placeholder="请输入安全防护措施"
                         value={formData.safetyMeasures}
                         onChange={(e) => handleFormChange('safetyMeasures', e.target.value)}
                         rows="3"
                         className={formErrors.safetyMeasures ? 'error' : ''}
                         disabled={isSubmitting || showSupervisorModal}
                       />
                       {formErrors.safetyMeasures && <span className="error-text">{formErrors.safetyMeasures}</span>}
                     </div>

                     <div className="form-item full-width">
                       <label>备注 <span className="required">*</span></label>
                       <textarea
                         placeholder="请输入备注"
                         value={formData.remarks}
                         onChange={(e) => handleFormChange('remarks', e.target.value)}
                         rows="3"
                         className={formErrors.remarks ? 'error' : ''}
                         disabled={isSubmitting || showSupervisorModal}
                       />
                       {formErrors.remarks && <span className="error-text">{formErrors.remarks}</span>}
                     </div>
                   </div>

                   <div className="modal-actions">
                     <button 
                       className="submit-btn" 
                       onClick={handleFormSubmit}
                       disabled={isSubmitting || showSupervisorModal}
                     >
                       {isSubmitting ? '提交中...' : '提交'}
                     </button>
                   </div>
                 </div>
               </div>
             </div>
           )}

           {/* 使用模板模态框 */}
           {showTemplateModal && (
             <div className="template-modal-overlay">
               <div className="template-modal">
                 <div className="modal-header">
                   <h3>模板管理</h3>
                   <button className="close-btn" onClick={() => setShowTemplateModal(false)}>×</button>
                 </div>
                 <div className="template-header-actions">
                   <button 
                     className="btn btn-secondary"
                     onClick={openPublicTemplatesModal}
                   >
                     浏览公共模板
                   </button>
                 </div>
                 <div className="modal-content">
                  <div className="template-search-section">
                    <input
                      type="text"
                      placeholder="搜索模板（可搜索模板名称、作业标题、作业区域、作业内容、安全措施、备注）"
                      value={userTemplateSearchText}
                      onChange={(e) => setUserTemplateSearchText(e.target.value)}
                      className="template-search-input"
                    />
                  </div>
                   {isLoadingTemplates ? (
                     <div className="loading-text">加载中...</div>
                   ) : userTemplates.length === 0 ? (
                     <div className="no-templates">
                       <p>暂无保存的模板</p>
                       <p>填写表单后可以保存为模板，方便下次使用</p>
                     </div>
                   ) : getFilteredUserTemplates().length === 0 ? (
                     <div className="no-templates">
                       <p>没有找到匹配的模板</p>
                       <p>请尝试其他搜索关键词</p>
                     </div>
                   ) : (
                     <div className="templates-list">
                       {getFilteredUserTemplates().map(template => (
                         <div key={template.id} className="template-item">
                           <div className="template-info">
                             <div className="template-name">
                               {template.name}
                               {template.isShared && <span className="shared-badge">已分享</span>}
                               {template.lastUsedAtText && <span className="shared-badge">最近使用</span>}
                             </div>
                             <div className="template-preview">
                               <div className="preview-item"><strong>作业标题:</strong> {template.workTitle}</div>
                               <div className="preview-item"><strong>作业区域:</strong> {template.workArea.substring(0, 50)}{template.workArea.length > 50 ? '...' : ''}</div>
                               <div className="preview-item"><strong>作业内容:</strong> {template.workContent.substring(0, 50)}{template.workContent.length > 50 ? '...' : ''}</div>
                               <div className="preview-item"><strong>安全防护措施:</strong> {template.safetyMeasures.substring(0, 50)}{template.safetyMeasures.length > 50 ? '...' : ''}</div>
                               <div className="preview-item"><strong>备注:</strong> {template.remarks.substring(0, 50)}{template.remarks.length > 50 ? '...' : ''}</div>
                             </div>
                             <div className="template-date">更新时间: {formatTemplateDateTime(template.updatedAtRaw || template.updatedAt)}</div>
                             {template.lastUsedAtText && <div className="template-date">最近使用: {formatTemplateDateTime(template.lastUsedAt || template.lastUsedAtText)}</div>}
                           </div>
                           <div className="template-actions">
                             <button 
                               className="apply-btn"
                               onClick={() => applyTemplate(template)}
                             >
                               使用
                             </button>
                             {template.isShared ? (
                               <button 
                                 className="unshare-btn"
                                 onClick={() => unshareTemplate(template.id, template.name)}
                               >
                                 取消分享
                               </button>
                             ) : (
                               <button 
                                 className="share-btn"
                                 onClick={() => shareTemplate(template.id, template.name)}
                               >
                                 分享
                               </button>
                             )}
                             <button 
                               className="delete-btn"
                               onClick={() => deleteTemplate(template.id, template.name)}
                             >
                               <FiTrash2 />
                             </button>
                           </div>
                         </div>
                       ))}
                     </div>
                   )}
                 </div>
               </div>
             </div>
           )}

           {/* 公共模板浏览模态框 */}
           {showPublicTemplatesModal && (
             <div className="template-modal-overlay">
               <div className="template-modal">
                 <div className="modal-header">
                   <h3>公共模板</h3>
                   <button className="close-btn" onClick={() => setShowPublicTemplatesModal(false)}>×</button>
                 </div>
                 <div className="modal-content">
                   {/* 搜索输入框 */}
                   <div className="template-search-section">
                     <input
                       type="text"
                       placeholder="搜索模板（可搜索模板名称、作者、作业标题、作业区域、作业内容、安全措施、备注）"
                       value={publicTemplateSearchText}
                       onChange={(e) => setPublicTemplateSearchText(e.target.value)}
                       className="template-search-input"
                     />
                   </div>
                   
                   {isLoadingPublicTemplates ? (
                     <div className="loading-text">加载中...</div>
                   ) : publicTemplates.length === 0 ? (
                     <div className="no-templates">
                       <p>暂无公共模板</p>
                       <p>其他用户分享的模板会显示在这里</p>
                     </div>
                   ) : getFilteredPublicTemplates().length === 0 ? (
                     <div className="no-templates">
                       <p>没有找到匹配的模板</p>
                       <p>请尝试其他搜索关键词</p>
                     </div>
                   ) : (
                     <div className="templates-list">
                       {getFilteredPublicTemplates().map(template => (
                         <div key={template.id} className="template-item">
                           <div className="template-info">
                             <div className="template-name">
                               {template.name}
                               <span className="author-badge">by {template.author}</span>
                             </div>
                             <div className="template-preview">
                               <div className="preview-item"><strong>作业标题:</strong> {template.workTitle}</div>
                               <div className="preview-item"><strong>作业区域:</strong> {template.workArea.substring(0, 50)}{template.workArea.length > 50 ? '...' : ''}</div>
                               <div className="preview-item"><strong>作业内容:</strong> {template.workContent.substring(0, 50)}{template.workContent.length > 50 ? '...' : ''}</div>
                               <div className="preview-item"><strong>安全防护措施:</strong> {template.safetyMeasures.substring(0, 50)}{template.safetyMeasures.length > 50 ? '...' : ''}</div>
                               <div className="preview-item"><strong>备注:</strong> {template.remarks.substring(0, 50)}{template.remarks.length > 50 ? '...' : ''}</div>
                             </div>
                             <div className="template-date">分享时间: {new Date(template.sharedAt).toLocaleString()}</div>
                           </div>
                           <div className="template-actions">
                             <button 
                               className="copy-btn"
                               onClick={() => openCopyTemplateModal(template)}
                             >
                               复制到我的模板
                             </button>
                           </div>
                         </div>
                       ))}
                     </div>
                   )}
                 </div>
               </div>
             </div>
           )}

           {/* 复制模板模态框 */}
           {showCopyTemplateModal && (
             <div className="save-template-modal-overlay">
               <div className="save-template-modal">
                 <div className="modal-header">
                   <h3>复制模板</h3>
                   <button className="close-btn" onClick={() => setShowCopyTemplateModal(false)}>×</button>
                 </div>
                 <div className="modal-content">
                   <div className="form-group">
                     <label>模板名称:</label>
                     <input
                       type="text"
                       value={copyTemplateName}
                       onChange={(e) => setCopyTemplateName(e.target.value)}
                       placeholder="请输入模板名称"
                       maxLength="50"
                     />
                   </div>
                   <div className="template-preview-info">
                     <h4>原模板信息:</h4>
                     <p><strong>名称:</strong> {selectedPublicTemplate?.name}</p>
                     <p><strong>作者:</strong> {selectedPublicTemplate?.author}</p>
                     <p><strong>作业标题:</strong> {selectedPublicTemplate?.workTitle}</p>
                   </div>
                 </div>
                 <div className="modal-footer">
                   <button 
                     className="btn btn-primary"
                     onClick={copyPublicTemplate}
                     disabled={!copyTemplateName.trim()}
                   >
                     确认复制
                   </button>
                 </div>
               </div>
             </div>
           )}

           {/* 保存模板模态框 */}
           {showSaveTemplateModal && (
             <div className="save-template-modal-overlay">
               <div className="save-template-modal">
                 <div className="modal-header">
                   <h3>保存为模板</h3>
                   <button className="close-btn" onClick={() => setShowSaveTemplateModal(false)}>×</button>
                 </div>
                 <div className="modal-content">
                   <div className="form-item">
                     <label>模板名称 <span className="required">*</span></label>
                     <input
                       type="text"
                       placeholder="请输入模板名称"
                       value={templateName}
                       onChange={(e) => setTemplateName(e.target.value)}
                       maxLength={50}
                     />
                   </div>
                   <div className="template-content-preview">
                     <h4>将保存以下内容：</h4>
                     <div className="preview-item"><strong>作业标题:</strong> {formData.workTitle || '(未填写)'}</div>
                     <div className="preview-item"><strong>作业区域:</strong> {formData.workArea || '(未填写)'}</div>
                     <div className="preview-item"><strong>作业内容:</strong> {formData.workContent || '(未填写)'}</div>
                     <div className="preview-item"><strong>安全防护措施:</strong> {formData.safetyMeasures || '(未填写)'}</div>
                     <div className="preview-item"><strong>备注:</strong> {formData.remarks || '(未填写)'}</div>
                   </div>
                   <div className="modal-actions">
                     <button 
                       className="save-btn"
                       onClick={saveTemplate}
                     >
                       保存
                     </button>
                   </div>
                 </div>
               </div>
             </div>
           )}

           {/* 编辑施工作业模态框 */}
           {showEditModal && (
             <div className="create-modal-overlay">
               <div className="create-modal">
                 <div className="modal-header">
                   <div className="header-title">
                     <h3>编辑{activeTab === 'C2' ? 'C2-1' : 'B3'}类施工作业</h3>
                   </div>
                   <div className="header-close">
                     <button 
                       className="close-btn" 
                       onClick={() => {
                         setShowEditModal(false);
                         setEditingItem(null);
                         setOriginalEditData(null);
                       }}
                       disabled={isEditSubmitting || showSupervisorModal}
                     >
                       ×
                     </button>
                   </div>
                 </div>
                 <div className="modal-content">
                   <div className="form-grid">
                     <div className="form-item">
                       <label>线路 <span className="required">*</span></label>
                       <select
                         value={formData.line}
                         disabled={true}
                         className="disabled-field"
                       >
                         <option value={formData.line}>{formData.line}</option>
                       </select>
                     </div>

                     <div className="form-item">
                        <label>{activeTab === 'B3' ? '作业车场' : '作业车站'} <span className="required">*</span></label>
                        <select
                           value={formData.station}
                           onChange={(e) => handleFormChange('station', e.target.value)}
                           disabled={isEditSubmitting || showSupervisorModal}
                           className={formErrors.station ? 'error' : ''}
                         >
                          <option value="">{activeTab === 'B3' ? '请选择车场' : '请选择车站'}</option>
                          {/* 如果当前选中的车站不在列表中，先添加它作为选项 */}
                          {formData.station && !((activeTab === 'C2' ? stations : depots).includes(formData.station)) && (
                            <option key={formData.station} value={formData.station}>{formData.station}</option>
                          )}
                          {activeTab === 'C2' ? (
                            stations.map(station => (
                              <option key={station} value={station}>{station}</option>
                            ))
                          ) : (
                            depots.map(depot => (
                              <option key={depot} value={depot}>{depot}</option>
                            ))
                          )}
                        </select>
                        {formErrors.station && <span className="error-text">{formErrors.station}</span>}
                      </div>

                     <div className="form-item">
                       <label>作业标题 <span className="required">*</span></label>
                       <input
                           type="text"
                           placeholder="请输入作业标题"
                           value={formData.workTitle}
                           onChange={(e) => handleFormChange('workTitle', e.target.value)}
                           className={formErrors.workTitle ? 'error' : ''}
                           disabled={isEditSubmitting || showSupervisorModal}
                         />
                       {formErrors.workTitle && <span className="error-text">{formErrors.workTitle}</span>}
                     </div>

                     <div className="form-item">
                       <label>作业日期 <span className="required">*</span></label>
                       <input
                         type="text"
                         value={formData.workDate}
                         disabled={true}
                         className="disabled-field"
                       />
                     </div>

                     <div className="form-item time-item">
                       <label>开始时间 <span className="required">*</span></label>
                       <div className="time-input-group">
                         <input
                             type="text"
                             placeholder="hh:mm"
                             value={formData.startTime}
                             onChange={(e) => handleFormChange('startTime', e.target.value)}
                             className={formErrors.startTime ? 'error' : ''}
                             disabled={isEditSubmitting || showSupervisorModal}
                           />
                           <label className="checkbox-label">
                             <input
                               type="checkbox"
                               checked={formData.isStartNextDay}
                               onChange={(e) => handleFormChange('isStartNextDay', e.target.checked)}
                               disabled={isEditSubmitting || showSupervisorModal}
                             />
                             次日
                           </label>
                       </div>
                       {formErrors.startTime && <span className="error-text">{formErrors.startTime}</span>}
                     </div>

                     <div className="form-item time-item">
                       <label>结束时间 <span className="required">*</span></label>
                       <div className="time-input-group">
                         <input
                             type="text"
                             placeholder="hh:mm"
                             value={formData.endTime}
                             onChange={(e) => handleFormChange('endTime', e.target.value)}
                             className={formErrors.endTime ? 'error' : ''}
                             disabled={isEditSubmitting || showSupervisorModal}
                           />
                           <label className="checkbox-label">
                             <input
                               type="checkbox"
                               checked={formData.isEndNextDay}
                               onChange={(e) => handleFormChange('isEndNextDay', e.target.checked)}
                               disabled={isEditSubmitting || showSupervisorModal}
                             />
                             次日
                           </label>
                       </div>
                       {formErrors.endTime && <span className="error-text">{formErrors.endTime}</span>}
                     </div>

                     <div className="form-item quick-actions-container full-width">
                       <div className="quick-actions-row">
                         <span className="quick-label">时长：</span>
                         {selectedDurationText && (
                           <span className="quick-duration-summary">已选总时长：{selectedDurationText}</span>
                         )}
                         {QUICK_DURATION_OPTIONS.map(option => (
                           <button
                             key={option.value}
                             type="button"
                             className={`quick-btn ${selectedQuickDurations.includes(option.value) ? 'selected' : ''}`}
                             onClick={() => handleQuickDuration(option.value)}
                             disabled={isEditSubmitting || showSupervisorModal}
                           >
                             {option.label}
                           </button>
                         ))}
                       </div>
                       <div className="quick-actions-row">
                         <span className="quick-label">开始时间取整：</span>
                         {QUICK_ROUND_OPTIONS.map(option => (
                           <button
                             key={option.value}
                             type="button"
                             className="quick-btn"
                             onClick={() => handleQuickRound(option.value)}
                             disabled={isEditSubmitting || showSupervisorModal}
                           >
                             {option.label}
                           </button>
                         ))}
                       </div>
                     </div>

                     <div className="form-item">
                       <label>施工负责人 <span className="required">*</span></label>
                       <input
                           type="text"
                           placeholder="请输入施工负责人"
                           value={formData.supervisor}
                           onChange={(e) => handleFormChange('supervisor', e.target.value)}
                           className={formErrors.supervisor ? 'error' : ''}
                           disabled={isEditSubmitting || showSupervisorModal}
                         />
                         {formErrors.supervisor && <span className="error-text">{formErrors.supervisor}</span>}
                       </div>
 
                       <div className="form-item">
                         <label>作业人数 <span className="required">*</span></label>
                         <input
                           type="number"
                           placeholder="请输入作业人数"
                           value={formData.workerCount}
                           onChange={(e) => handleFormChange('workerCount', e.target.value)}
                           min="1"
                           className={formErrors.workerCount ? 'error' : ''}
                           disabled={isEditSubmitting || showSupervisorModal}
                         />
                       {formErrors.workerCount && <span className="error-text">{formErrors.workerCount}</span>}
                     </div>

                     <div className="form-item full-width">
                       <label>作业区域 <span className="required">*</span></label>
                       <textarea
                           placeholder="请输入作业区域"
                           value={formData.workArea}
                           onChange={(e) => handleFormChange('workArea', e.target.value)}
                           rows="5"
                           className={formErrors.workArea ? 'error' : ''}
                           disabled={isEditSubmitting || showSupervisorModal}
                         />
                         {formErrors.workArea && <span className="error-text">{formErrors.workArea}</span>}
                       </div>
 
                       <div className="form-item full-width">
                         <label>作业内容 <span className="required">*</span></label>
                         <textarea
                           placeholder="请输入作业内容"
                           value={formData.workContent}
                           onChange={(e) => handleFormChange('workContent', e.target.value)}
                           rows="5"
                           className={formErrors.workContent ? 'error' : ''}
                           disabled={isEditSubmitting || showSupervisorModal}
                         />
                         {formErrors.workContent && <span className="error-text">{formErrors.workContent}</span>}
                       </div>
 
                       <div className="form-item full-width">
                         <label>安全防护措施 <span className="required">*</span></label>
                         <textarea
                           placeholder="请输入安全防护措施"
                           value={formData.safetyMeasures}
                           onChange={(e) => handleFormChange('safetyMeasures', e.target.value)}
                           rows="3"
                           className={formErrors.safetyMeasures ? 'error' : ''}
                           disabled={isEditSubmitting || showSupervisorModal}
                         />
                         {formErrors.safetyMeasures && <span className="error-text">{formErrors.safetyMeasures}</span>}
                       </div>
 
                       <div className="form-item full-width">
                         <label>备注 <span className="required">*</span></label>
                         <textarea
                           placeholder="请输入备注"
                           value={formData.remarks}
                           onChange={(e) => handleFormChange('remarks', e.target.value)}
                           rows="3"
                           className={formErrors.remarks ? 'error' : ''}
                           disabled={isEditSubmitting || showSupervisorModal}
                         />
                       {formErrors.remarks && <span className="error-text">{formErrors.remarks}</span>}
                     </div>
                   </div>

                   <div className="modal-actions">
                     <button 
                       className="submit-btn" 
                       onClick={handleEditSubmit}
                       disabled={isEditSubmitting || showSupervisorModal}
                     >
                       {isEditSubmitting ? '保存中...' : '保存修改'}
                     </button>
                   </div>
                 </div>
               </div>
             </div>
           )}

           {/* 检修工单模态框 */}
           {showMaintenanceModal && (
             <div className="order-modal-overlay">
               <div className="order-modal">
                 <div className="modal-header">
                   <h3>添加检修工单号</h3>
                   <button 
                     className="close-btn" 
                     onClick={() => setShowMaintenanceModal(false)}
                     disabled={isLoadingMaintenanceOrders}
                   >
                     ×
                   </button>
                 </div>
                 <div className="modal-content">
                   
                   {maintenanceOrders.length > 0 && (
                     <div className="orders-list">
                       <div className="orders-header">
                         <label>
                           <input
                             type="checkbox"
                             checked={selectedMaintenanceOrders.length === maintenanceOrders.length}
                             onChange={(e) => {
                               if (e.target.checked) {
                                 setSelectedMaintenanceOrders([...maintenanceOrders]);
                               } else {
                                 setSelectedMaintenanceOrders([]);
                               }
                             }}
                           />
                           全选
                         </label>
                       </div>
                       <div className="orders-content">
                         {maintenanceOrders.map((order, index) => (
                           <div key={index} className="order-item">
                             <label>
                               <input
                                 type="checkbox"
                                 checked={selectedMaintenanceOrders.some(selected => selected.orderNumber === order.orderNumber)}
                                 onChange={(e) => {
                                   if (e.target.checked) {
                                     setSelectedMaintenanceOrders(prev => [...prev, order]);
                                   } else {
                                     setSelectedMaintenanceOrders(prev => 
                                       prev.filter(selected => selected.orderNumber !== order.orderNumber)
                                     );
                                   }
                                 }}
                               />
                               <div className="order-info">
                                 <div className="order-number">工单号：{order.orderNumber}</div>
                                 <div className="order-description">描述：{order.description}</div>
                                 <div className="order-details">
                                   <span>计划开始时间：{order.planStartTime}</span>
                                   <span>维护周期：{order.maintenanceCycle}</span>
                                 </div>
                               </div>
                             </label>
                           </div>
                         ))}
                       </div>
                     </div>
                   )}
                   
                   {isLoadingMaintenanceOrders && (
                     <div className="loading-text">正在查询检修工单...</div>
                   )}
                 </div>
                 <div className="modal-actions">
                   <button 
                     className="confirm-btn"
                     onClick={confirmMaintenanceOrders}
                     disabled={selectedMaintenanceOrders.length === 0}
                   >
                     确定（已选{selectedMaintenanceOrders.length}个）
                   </button>
                 </div>
               </div>
             </div>
           )}

           {/* 故障工单模态框 */}
           {showFaultModal && (
             <div className="order-modal-overlay">
               <div className="order-modal">
                 <div className="modal-header">
                   <h3>添加故障工单号</h3>
                   <button 
                     className="close-btn" 
                     onClick={() => setShowFaultModal(false)}
                     disabled={isLoadingFaultOrders}
                   >
                     ×
                   </button>
                 </div>
                 <div className="modal-content">
                   
                   {faultOrders.length > 0 && (
                     <div className="orders-list">
                       <div className="orders-header">
                         <label>
                           <input
                             type="checkbox"
                             checked={selectedFaultOrders.length === faultOrders.length}
                             onChange={(e) => {
                               if (e.target.checked) {
                                 setSelectedFaultOrders([...faultOrders]);
                               } else {
                                 setSelectedFaultOrders([]);
                               }
                             }}
                           />
                           全选
                         </label>
                       </div>
                       <div className="orders-content">
                         {faultOrders.map((order, index) => (
                           <div key={index} className="order-item">
                             <label>
                               <input
                                 type="checkbox"
                                 checked={selectedFaultOrders.some(selected => selected.orderNumber === order.orderNumber)}
                                 onChange={(e) => {
                                   if (e.target.checked) {
                                     setSelectedFaultOrders(prev => [...prev, order]);
                                   } else {
                                     setSelectedFaultOrders(prev => 
                                       prev.filter(selected => selected.orderNumber !== order.orderNumber)
                                     );
                                   }
                                 }}
                               />
                               <div className="order-info">
                                 <div className="order-number">工单号：{order.orderNumber}</div>
                                 <div className="order-description">描述：{order.description}</div>
                                 <div className="order-details">
                                   <span>故障提报时间：{order.faultReportTime}</span>
                                 </div>
                               </div>
                             </label>
                           </div>
                         ))}
                       </div>
                     </div>
                   )}
                   
                   {isLoadingFaultOrders && (
                     <div className="loading-text">正在查询故障工单...</div>
                   )}
                 </div>
                 <div className="modal-actions">
                   <button 
                     className="confirm-btn"
                     onClick={confirmFaultOrders}
                     disabled={selectedFaultOrders.length === 0}
                   >
                     确定（已选{selectedFaultOrders.length}个）
                   </button>
                 </div>
               </div>
             </div>
           )}

           {/* 施工作业台账表格 */}
           <section className="construction-table-section">
             <div className="table-header">
               <h2 className="table-title">{activeTab}类施工作业台账</h2>
               <div className="table-actions">
                 <button className="create-btn" onClick={() => handleCreate()}>
                   <FiPlus className="create-icon" />
                   新建{activeTab === 'C2' ? 'C2-1' : 'B3'}类
                 </button>
                 <div className="action-buttons">
                   <button 
                     className="fetch-oa-btn" 
                     onClick={fetchOAData}
                     disabled={isLoadingOAData}
                   >
                     {isLoadingOAData ? '刷新中...' : '刷新数据'}
                   </button>
                   <button 
                     className="filter-btn" 
                     onClick={() => setShowFilterModal(true)}
                   >
                     <FiFilter /> 筛选
                   </button>
                 </div>
                 <div className="table-info">
                   <span>共 {constructionData.length} 条记录</span>
                 </div>
               </div>
             </div>
            
            <div className="table-container">
              {/* 桌面端表格 */}
              <table className="construction-table">
                <thead>
                  <tr>
                    <th className="no-wrap-col">线路</th>
                    <th className="no-wrap-col">作业代码</th>
                    <th className="no-wrap-col">作业日期</th>
                    <th className="no-wrap-col">作业时间</th>
                    <th>作业部门</th>
                    <th>{activeTab === 'B3' ? '作业车场' : '作业车站'}</th>
                    <th>作业内容</th>
                    <th>作业区域</th>
                    <th>施工负责人</th>
                    <th>业务状态</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {constructionData.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="no-data">
                        <div className="no-data-message">
                          <span>暂无数据</span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    constructionData.map((item) => (
                      <tr key={item.id}>
                        <td className="no-wrap-col">{item.line}</td>
                        <td className="no-wrap-col">{item.workCode}</td>
                        <td className="no-wrap-col">{item.workDate}</td>
                        <td className="no-wrap-col">{item.workTime}</td>
                        <td>{item.department}</td>
                        <td>{item.station}</td>
                        <td>{item.content}</td>
                        <td>{item.area}</td>
                        <td>{item.supervisor}</td>
                        <td>
                          <span className={`status-badge ${getStatusColor(item.status)}`}>
                            {item.status}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button 
                              className={`action-btn copy-btn ${copyingItemId === item.uniqueId ? 'copying' : ''}`}
                              onClick={() => handleCopy(item)}
                              disabled={copyingItemId === item.uniqueId || isGlobalButtonsDisabled}
                              title={copyingItemId === item.uniqueId ? "复制中..." : "复制"}
                            >
                              {copyingItemId === item.uniqueId ? (
                                <div className="loading-spinner"></div>
                              ) : (
                                <FiCopy />
                              )}
                            </button>
                            {item.actions?.canEdit && (
                              <button 
                              className={`action-btn edit-btn ${editingItemId === item.uniqueId ? 'editing' : ''}`}
                              onClick={() => handleEdit(item)}
                              disabled={editingItemId === item.uniqueId || isGlobalButtonsDisabled}
                              title={editingItemId === item.uniqueId ? "获取编辑数据中..." : "编辑"}
                            >
                              {editingItemId === item.uniqueId ? (
                                <div className="loading-spinner"></div>
                              ) : (
                                <FiEdit2 />
                              )}
                            </button>
                          )}
                          {item.actions?.canDelete && (
                            <button 
                              className={`action-btn delete-btn ${deletingItemId === item.uniqueId ? 'deleting' : ''}`}
                              onClick={() => handleDelete(item)}
                              disabled={deletingItemId === item.uniqueId || isGlobalButtonsDisabled}
                              title={deletingItemId === item.uniqueId ? "正在删除..." : "删除"}
                            >
                              <FiTrash2 />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                  )}
                </tbody>
              </table>
              
              {/* 手机端卡片式布局 */}
              <div className="mobile-data-cards">
                {constructionData.length === 0 ? (
                  <div className="mobile-data-card">
                    <div className="no-data-message">
                      <span>暂无数据</span>
                    </div>
                  </div>
                ) : (
                  constructionData.map((item) => (
                    <div key={item.id} className="mobile-data-card">
                      <div className="card-header">
                        <div className="card-title">{item.content}</div>
                        <div className="card-actions">
                          <button 
                            className={`action-btn copy-btn ${copyingItemId === item.uniqueId ? 'copying' : ''}`}
                            onClick={() => handleCopy(item)}
                            disabled={copyingItemId === item.uniqueId || isGlobalButtonsDisabled}
                            title={copyingItemId === item.uniqueId ? "复制中..." : "复制"}
                          >
                            {copyingItemId === item.uniqueId ? (
                              <div className="loading-spinner"></div>
                            ) : (
                              <FiCopy />
                            )}
                          </button>
                          {item.actions?.canEdit && (
                            <button 
                              className={`action-btn edit-btn ${editingItemId === item.uniqueId ? 'editing' : ''}`}
                              onClick={() => handleEdit(item)}
                              disabled={editingItemId === item.uniqueId || isGlobalButtonsDisabled}
                              title={editingItemId === item.uniqueId ? "获取编辑数据中..." : "编辑"}
                            >
                              {editingItemId === item.uniqueId ? (
                                <div className="loading-spinner"></div>
                              ) : (
                                <FiEdit2 />
                              )}
                            </button>
                          )}
                          {item.actions?.canDelete && (
                            <button 
                              className={`action-btn delete-btn ${deletingItemId === item.uniqueId ? 'deleting' : ''}`}
                              onClick={() => handleDelete(item)}
                              disabled={deletingItemId === item.uniqueId || isGlobalButtonsDisabled}
                              title={deletingItemId === item.uniqueId ? "删除中..." : "删除"}
                            >
                              {deletingItemId === item.uniqueId ? (
                                <div className="loading-spinner"></div>
                              ) : (
                                <FiTrash2 />
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="card-content">
                        <div className="card-field">
                          <div className="field-label">线路</div>
                          <div className="field-value">{item.line}</div>
                        </div>
                        <div className="card-field">
                          <div className="field-label">作业代码</div>
                          <div className="field-value">{item.workCode}</div>
                        </div>
                        <div className="card-field">
                          <div className="field-label">作业日期</div>
                          <div className="field-value">{item.workDate}</div>
                        </div>
                        <div className="card-field">
                          <div className="field-label">作业时间</div>
                          <div className="field-value">{item.workTime}</div>
                        </div>
                        <div className="card-field">
                          <div className="field-label">作业部门</div>
                          <div className="field-value">{item.department}</div>
                        </div>
                        <div className="card-field">
                          <div className="field-label">{activeTab === 'B3' ? '作业车场' : '作业车站'}</div>
                          <div className="field-value">{item.station}</div>
                        </div>
                        <div className="card-field full-width">
                          <div className="field-label">作业区域</div>
                          <div className="field-value">{item.area}</div>
                        </div>
                        <div className="card-field full-width">
                          <div className="field-label">施工负责人</div>
                          <div className="field-value">{item.supervisor}</div>
                        </div>
                        <div className="card-field">
                          <div className="field-label">业务状态</div>
                          <div className="field-value status">
                            <span className={`status-badge ${getStatusColor(item.status)}`}>
                              {item.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </section>
        </div>
      </main>
      
      {/* 底部导航栏 */}
      <nav className={`bottom-nav ${showEditModal || isSubmitting ? 'disabled' : ''}`}>
        <button 
          className={`nav-item ${activeTab === 'C2' ? 'active' : ''} ${showEditModal || isSubmitting ? 'disabled' : ''}`}
          onClick={() => !(showEditModal || isSubmitting) && setActiveTab('C2')}
          disabled={showEditModal || isSubmitting}
        >
          <span className="nav-icon">🚇</span>
          <span className="nav-text">C2类施工</span>
        </button>
        <button 
          className={`nav-item ${activeTab === 'B3' ? 'active' : ''} ${showEditModal || isSubmitting ? 'disabled' : ''}`}
          onClick={() => !(showEditModal || isSubmitting) && setActiveTab('B3')}
          disabled={showEditModal || isSubmitting}
        >
          <span className="nav-icon">🚧</span>
          <span className="nav-text">B3类施工</span>
        </button>
      </nav>
      
      {/* 公告提示框 */}
      <AnnouncementModal announcement={announcement} />
    </div>
  );
};

export default DashboardPage;
